import { estateOf } from '../../../../shared/src/calculation/support/estateCgt.ts';
import { chosenPlanOutOfDate, markRecordOutOfDate, NOT_FOUND_ON_RETEST, profileFingerprint, retestChosenRecord } from '../../utils/chosenPlanRefresh.ts';
import React, { useState, useMemo, useEffect, useRef } from 'react';
import { householdVoice } from '../../utils/householdWords.ts';
import { scrollAppToTop } from '../../utils/scrollAppToTop.ts';
import { MODULE_ICONS } from '../../components/icons/moduleIcons.ts';
import { ChevronLeft, ArrowRight, Home, Wallet, Users, ShieldCheck, Scale } from 'lucide-react';
import StrategyRisksPanel from '../../components/strategy/StrategyRisksPanel.tsx';
import { buildStrategyRisks, riskKindsFromCandidateId, gfRiskKinds } from '../../strategy/strategyRisks.ts';
import { alternativeAssetNote } from '../../strategy/alternativeAssetNote.ts';
import { buildGrannyFlatSoaSnapshot, gfFamilyToKeepWhole } from './gfSoa.ts';
import { openModuleSoa } from '../advice/moduleSoa.ts';
import { takeGrannyFlatOpenSection, PAGE_RESELECTED_EVENT, gfHostKindOf, gfHostPhrase } from './gfPresentation.ts';
import { Card, GradientText, FeeExplanationAccordion, Button, IconTile } from '../../components/ui/index.tsx';
import * as calculationApi from '../../services/calculationApi.ts';
import { useCalcTracker } from '../../utils/calcTracker.ts';
import { getGrannyFlatDeprivationFromFactFind, grannyFlatHomeGiven, loansSecuredOnHome } from '../../../../shared/src/calculation/grannyFlatArrangement.ts';
import { getRecommendedEmergencyFund } from '../../utils/emergencyFund.ts';
import { describeResidualDeployment } from '../../../../shared/src/calculation/residualDeployment.ts';
import { buildGrannyFlatSuggestions, getGrannyFlatReasonableValueForClient, gfEngineSetupFromGoals, gfRentAfterVacancy, DEFAULT_GF_VACANCY_WEEKS, gfEffectiveStartDate } from '../../strategy/grannyFlatRecommendedStrategies.ts';
import type { GrannyFlatConfig, GrannyFlatSuggestionsResult } from '../../strategy/grannyFlatRecommendedStrategies.ts';
import { enrichStrategiesWithRecommendationLabels, buildRecommendedOptimalStrategies, mergeGoalLeaderStrategies, type RecommendedStrategyEntry } from '../../strategy/supportAtHomeRecommendedStrategies.ts';
import { candidateReadings } from '../../strategy/planChoiceRule.ts';
import type { GoalFocusId } from '../agedCare/EngineGoalControl.tsx';
import AnalyserSteps, { type AnalyserStep } from '../../components/strategy/AnalyserSteps.tsx';
import { annuityHorizonFlag, type HorizonFactFind } from '../agedCare/strategyHorizonFlags.ts';
import TransferCapGate from '../../components/TransferCapGate.tsx';
import { tbcQuestionNeededFor } from '../../../../shared/src/calculation/transferBalanceCap.ts';
import { nonConcessionalCapacityForHousehold } from '../../../../shared/src/calculation/nonConcessionalContributions.ts';
import { buildNetWorthSchedule } from '../../../../shared/src/calculation/netWorthSchedule.ts';
import { formatCurrency } from '../../utils/index.ts';
import { EXTRA_ALLOWABLE_AMOUNT, PENSION_RATES } from '../../constants/index.ts';
import GrannyFlatPlanRecap from './GrannyFlatPlanRecap.tsx';
import type { Financials, ProjectionYear, GrannyFlatInsightFinancials, MoneyFlowRow, SaleFlow, GfStrategyView } from './grannyFlatTypes.ts';
import SuggestionEngineAnalysisLoader from '../../components/ui/SuggestionEngineAnalysisLoader.tsx';
import NewMoneyProfileGate from '../../components/NewMoneyProfileGate.tsx';
import { hasAnsweredNewMoneyProfile } from '../../../../shared/src/calculation/investmentProfiles.ts';
import type { InvestmentProfileId } from '../../../../shared/src/calculation/investmentProfiles.ts';
import { deploymentAnnuityTerms, deploymentIsIncomeOnly } from '../../components/strategy/ProceedsDeploymentFields.tsx';
import { IncomeOnlyBeforeChoose, incomeOnlyHeld, incomeOnlySetter } from '../../components/strategy/IncomeOnlyTick.tsx';
import { EngineAnnuityTerms, AnnuityQuoteControl } from '../../components/strategy/AnnuityTerms.tsx';
import AnnuityFamilyPanel from '../../components/strategy/AnnuityFamilyPanel.tsx';
import { annuityQuoteShapeOf, annuityQuoteNote, quotedIncomeFor, type AnnuityQuote } from '../../strategy/annuityQuote.ts';
import { gfGuaranteedIncomeOf, gfComparePlanFor, GF_COMPARE_NOTE } from './gfGuaranteedIncome.ts';
import type { ProceedsDeploymentValue } from '../../components/strategy/ProceedsDeploymentFields.tsx';
import ProjectionsModal from '../agedCare/ProjectionsModal.tsx';
import { readPeriodMode, type ProjectionsModalPeriodMode } from '../agedCare/projectionsModalPreferences.ts';
import PlanExplanationModal from '../agedCare/PlanExplanationModal.tsx';
import { planRunwayCopy, summariseFunding } from '../../components/insights/fundingSummary.tsx';
import { buildMoneyStory, MoneyStoryBlock } from '../agedCare/moneyStory.tsx';
import { CauseEffectLedger, ChangeOrText, putTogetherLine } from '../../components/strategy/CauseEffectLedger.tsx';
import DialogShell, { DialogSection } from '../../components/ui/DialogShell.tsx';
import DebtPayoffStrategy, { payableDebtsOf } from '../agedCare/DebtPayoffStrategy.tsx';
import { applyDebtPayoffToFactFind, type DebtPayoff } from '../../../../shared/src/calculation/support/debtPayoff.ts';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import type { FactFind } from '../../../../shared/src/calculation/factFindTypes.ts';
import type { ResidualDeployment } from '../../../../shared/src/calculation/residualDeployment.ts';
import GfStep1, { gfGiveOf } from './GfStep1.tsx';
import GfStep2 from './GfStep2.tsx';
import GfQuestions, { gfAnswersToGoals } from './GfQuestions.tsx';
import GfPlansView, { type GfSetAside } from './GfPlansView.tsx';
import GfHub from './GfHub.tsx';
import GfBuildPlan, { type GfGiveForm, type GfHomeUse } from './GfBuildPlan.tsx';
import GfFamilyGuide, { gfGuideFormOf } from './GfFamilyGuide.tsx';
import { gfPlanWords, gfMoveOf, gfGivenOf, gfExplainSteps } from './gfPlanWords.ts';
import { chosenGfPlanOf, withChosenGfPlan, gfGuideFiguresOf, gfFamilyRecordOf, GF_START_FRESH_KEY, type GfPlanCardRecord, type ChosenGfPlan } from './gfChosenPlan.ts';
import { openGfSolicitorBrief } from './gfSolicitorBrief.ts';
import DebtsInPlanNote, { clearableDebtTotal } from '../../components/strategy/DebtsInPlanNote.tsx';
import RentedHomeCgtNote from '../../components/strategy/RentedHomeCgtNote.tsx';
import HowItIsInvestedNote from '../../components/strategy/HowItIsInvestedNote.tsx';
import { plansInvest } from '../../utils/investedWhere.ts';

export type { GfStrategyView } from './grannyFlatTypes.ts';

/** The non-null result of getGrannyFlatDeprivationFromFactFind (reasonable-value / deprivation breakdown). */
type DepResult = NonNullable<ReturnType<typeof getGrannyFlatDeprivationFromFactFind>>;

interface ExplainSection {
  id: string;
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  summary: string;
  details: React.ReactNode;
  /** null = no pill: these are general sections, not a finding about this client (FeeExplanationAccordion). */
  applies: boolean | 'optional' | null;
}

/**
 * The investment property line is shown only to a client who has one on file (owner, 2 Oct 2026:
 * no need to confuse the others). The face-value forms name the title of the HOME; an investment
 * property signed over is assets given, and tested.
 */
const gfExplainSections = (hasInvestmentProperty: boolean): ExplainSection[] => [
  { id: 'what', icon: Home, title: 'What a granny flat arrangement is', summary: 'You give money or assets to family in exchange for a lifetime right to live in their home. It is a Centrelink concept — not necessarily a separate flat.', details: <p className="text-gray-700 leading-relaxed">A &quot;granny flat interest&quot; (or right) is a formal arrangement where you transfer money and/or assets — sometimes your own home — to a family member, in return for the right to live in their home for life. You don&apos;t have to build an actual granny flat. It can be very tax- and pension-effective, but it needs to be set up carefully.</p>, applies: null },
  { id: 'homeowner', icon: Wallet, title: 'Test 1 — Will Centrelink treat you as a homeowner?', summary: `Two separate tests apply, and this is the first. It asks one thing: how much you give in total. Above the extra allowable amount ($${EXTRA_ALLOWABLE_AMOUNT.toLocaleString()}), you're a homeowner and the right to live there is exempt from the assets test, like a home. At or below it, you're a non-homeowner.`, details: <div className="text-gray-700 leading-relaxed space-y-2"><p>This test only decides <strong>which Age Pension limits apply to you</strong>. Add up everything you give — cash, the cost of any build you pay for, and any home you transfer. If the total is <strong>above</strong> the extra allowable amount (currently ${EXTRA_ALLOWABLE_AMOUNT.toLocaleString()}), Centrelink treats you as a <strong>homeowner</strong>: your right to live there is exempt from the assets test, just as a home would be, and the lower homeowner asset limits apply. If the total is <strong>at or below</strong> it, you&apos;re a <strong>non-homeowner</strong>: the right counts as an asset, the higher non-homeowner limits apply, and you may qualify for Rent Assistance.</p><p>Because you&apos;re turning counted savings into an exempt right, your Age Pension often <strong>goes up</strong>. But "exempt" is about the right you receive. It says nothing about whether you paid too much for it — that&apos;s the second test, below.</p></div>, applies: null },
  { id: 'reasonable', icon: Scale, title: 'Test 2 — Did you pay more than the right is worth? (the gift test)', summary: 'The second test asks a different question: did you give more than Centrelink says the right is worth? It depends on what you give. Sign over your home, or pay to build the accommodation, and Centrelink takes that at face value — no gift, whatever it costs. Pay cash and there is a limit: up to Centrelink\'s reasonable value nothing is a gift; above it, the excess is counted against you for five years.', details: <div className="text-gray-700 leading-relaxed space-y-2"><p><strong>Three ways of paying are taken at face value.</strong> If all you do is sign over the title of your home, pay for the building or extension of your accommodation, or buy a home in the family member&apos;s name, Centrelink accepts what that is worth as the price of the right. There is no gift, however large the amount — a $600,000 build is a $600,000 right. Keep the building contract and invoices: anything paid beyond the actual cost of the build counts as extra.</p><p><strong>Cash is tested.</strong> Pay cash, or add cash or other assets on top of one of the three above, and Centrelink works out a <strong>reasonable value</strong> for your lifetime right: the combined partnered pension rate multiplied by a factor for your age. The right is valued at the greater of that formula and what you provided (the home, the build). Whatever you give <strong>up to that value</strong> is the price of the right — no gift. The excess is treated as a gift: after the usual free area ($10,000 a year, $30,000 over five years) it is counted in your assets and deemed in your income for <strong>five years</strong>, then drops off.</p>{hasInvestmentProperty && <p><strong>Your investment property is not your home.</strong> The face-value rule is for the title of the home you live in. Sign an investment property over to family for the right and Centrelink treats it as assets given, like cash: the right is valued at the formula, and the property&apos;s value above that is a gift. Confirm this with Services Australia&apos;s Financial Information Service before relying on it.</p>}<p><strong>How the two tests fit together.</strong> Test 1 makes the right itself exempt. Test 2 catches any overpayment and counts it anyway. So you can be a homeowner with an exempt right <em>and</em> still have a gift counted against you, if you paid too much in cash. The plans that pay Centrelink&apos;s value sit exactly at its figure, so nothing is a gift.</p></div>, applies: null },
  { id: 'documentation', icon: ShieldCheck, title: 'Protect yourself — get it in writing', summary: 'An undocumented arrangement is risky: if things go wrong with the family member, you can lose both the money and the home.', details: <p className="text-gray-700 leading-relaxed">Always use a <strong>written agreement</strong>. If the family member divorces, dies, goes bankrupt, sells, or you fall out, an undocumented arrangement can leave you with no home and no money back. A written agreement makes your right enforceable and is what Centrelink expects. Get independent legal advice before transferring anything.</p>, applies: null },
  { id: 'estate', icon: Users, title: 'Fairness to other family', summary: 'Giving a large amount to one family member can leave others with much less — a common cause of will disputes. Plan for fairness.', details: <p className="text-gray-700 leading-relaxed">A granny-flat arrangement gives one family member a large slice of your wealth. If the rest of your estate is then split equally, the others can be left behind — a classic trigger for <strong>will disputes</strong>. Consider how to keep things fair (for example, adjusting your will) and talk to an estate-planning solicitor.</p>, applies: null },
];

interface GrannyFlatExplainedProps {
  onBack: () => void;
  onFamilyGuide: () => void;
  factFind: DraftFactFind;
}

/** How granny flat arrangements work — the full guide, a link from step 1 (the village's ruling: the steps first). */
const GrannyFlatExplained = ({ onBack, onFamilyGuide, factFind }: GrannyFlatExplainedProps) => {
  const [openAccordion, setOpenAccordion] = useState<string | null>(null);
  const toggle = (id: string) => setOpenAccordion(openAccordion === id ? null : id);
  const hasInvestmentProperty = (factFind?.assets?.investmentProperty || []).some((p) => (parseFloat(String(p?.value ?? '')) || 0) > 0);
  const sections = gfExplainSections(hasInvestmentProperty);
  return (
    <div className="max-w-6xl mx-auto" data-testid="gf-overview">
      <Button variant="ghost" size="sm" icon={ChevronLeft} onClick={onBack} className="mb-4 -ml-2">Back to the granny flat</Button>
      <div className="mb-8">
        <div className="flex items-center gap-4 mb-4">
          <IconTile icon={MODULE_ICONS.granny_flat} tone="brand" variant="solid" size="lg" className="hidden sm:inline-flex" />
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900"><GradientText>Granny flat arrangements</GradientText></h1>
        </div>
        <p className="text-lg text-slate-700 leading-relaxed mb-3">Moving in with family and giving them money or your home for the right to live there? A granny flat arrangement can be very pension- and tax-effective, but it must be set up carefully. This is how it works.</p>
        <p className="text-sm text-slate-500">This is general information. A granny flat arrangement is a significant legal and family decision — get independent legal, financial and estate-planning advice before you commit.</p>
      </div>
      <div className="space-y-4 mb-8">
        {sections.map((fee) => (
          <FeeExplanationAccordion key={fee.id} {...fee} isOpen={openAccordion === fee.id} onToggle={() => toggle(fee.id)} factFind={{}} />
        ))}
      </div>
      <Card className="mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between">
          <div className="flex items-start gap-3">
            <IconTile icon={Users} tone="brand" variant="soft" size="sm" className="hidden sm:inline-flex" />
            <div>
              <h3 className="text-xl font-bold text-gray-800">Talk it through as a family first</h3>
              <p className="text-sm text-gray-600">Questions to settle together before any money changes hands — what the right covers, who pays what, fairness to other family, and the &quot;what-ifs&quot; — with a brief you can print and hand to a solicitor.</p>
            </div>
          </div>
          <Button variant="secondary" size="sm" iconRight={ArrowRight} className="shrink-0" onClick={onFamilyGuide}>Open the guide</Button>
        </div>
      </Card>
      <div className="text-center">
        <Button onClick={onBack} variant="brand" size="lg" iconRight={ArrowRight}>Back to the granny flat</Button>
      </div>
    </div>
  );
};

/** Manual Control's state — the ledger ("Build a plan yourself") writes it; the projection reads it. */
interface GfInputs {
  amountPaid: number;
  transferHome: boolean;
  constructionCost: number;
  fundFrom: 'savings' | 'sell_home';
  funding: Record<string, number>;
  fundingCgtOverrides: Record<string, number | null>;
  deployment: ProceedsDeploymentValue;
  justMovingIn: boolean;
  keptHomeUse: 'vacant' | 'rented';
  /** The kept home's gross weekly rent, before the weeks it sits empty. */
  keptHomeRent: number;
  keptHomeExpenses: number;
  keptHomeVacancy: number;
}

interface GfBeforeAfter {
  before: Financials;
  after: Financials;
  simulated: DraftFactFind;
}

interface GrannyFlatFundingMeta {
  breakdown?: MoneyFlowRow[];
  cgt?: number;
  shortfall?: number;
}

interface SeView {
  name?: string;
  before: Financials;
  after: Financials;
  projections: ProjectionYear[];
  depResult: DepResult | null;
  hasArrangement: boolean;
  justMovingIn: boolean;
  amountPaid: number;
  propertyTransferred: number;
  constructionCost?: number;
  entryContribution: number;
  isHomeowner: boolean;
  keepingHome: boolean;
  keptHomeRented: boolean;
  formerHomeValue: number;
  reasonableValue: number;
  interestValue: number;
  deprivation: number;
  legacyEstimate: number;
  fundingRows: MoneyFlowRow[];
  fundingCgt: number;
  fundingShortfall: number;
  deploymentRows: MoneyFlowRow[];
  saleFlow: SaleFlow | null;
  insightFinancials: GrannyFlatInsightFinancials;
}

interface PanelModel {
  name?: string | null;
  before: Financials;
  after: Financials;
  simulated?: DraftFactFind;
  depResult?: DepResult | null;
  hasArrangement: boolean;
  justMovingIn: boolean;
  amountPaid: number;
  propertyTransferred: number;
  constructionCost?: number;
  entryContribution: number;
  isHomeowner: boolean;
  keepingHome: boolean;
  keptHomeRented: boolean;
  formerHomeValue: number;
  reasonableValue: number;
  interestValue: number;
  deprivation: number;
  legacyEstimate?: number;
  projections: ProjectionYear[];
  fundingRows: MoneyFlowRow[];
  fundingCgt: number;
  fundingShortfall: number;
  deploymentRows: MoneyFlowRow[];
  saleFlow: SaleFlow | null;
  insightFinancials: GrannyFlatInsightFinancials | null;
}

interface GrannyFlatAnalyserProps {
  setActivePage: (page: string) => void;
  factFind: DraftFactFind;
  /** App-owned saved strategies: the chosen plan is read from here (the plan page, Home). */
  savedStrategies?: ReadonlyArray<Record<string, unknown>>;
  setSavedStrategies?: React.Dispatch<React.SetStateAction<Array<Record<string, unknown>>>>;
  setFactFind?: React.Dispatch<React.SetStateAction<DraftFactFind>>;
  onSoaGenerated?: (document: Record<string, unknown>) => void;
  /** Where the page opens: the family guide, the full guide, or the steps (Home and the action plan). */
  opening?: 'guide' | 'overview' | 'calculator' | null;
}

/** The age-and-health warning for a candidate's annuity — the aged care rule, applied here. */
function gfAnnuityHorizonWarningOf(strategy: GfStrategyView, factFind: DraftFactFind): string | null {
  const terms = deploymentAnnuityTerms(strategy.inputs?.deployment as ProceedsDeploymentValue | undefined, factFind?.adviceFor === 'couple');
  if (!terms) return null;
  const flag = annuityHorizonFlag({ factFind: factFind as unknown as HorizonFactFind, residualCapital: (Number(terms.residualPercent) || 0) / 100, owner: terms.owner });
  return flag?.warning ?? null;
}

const num = (v: unknown): number => parseFloat(String(v ?? '')) || 0;

/** Step 1's and step 3's answers: what a new model from scratch takes off the file so nothing carries over. */
const GF_FRESH_GOAL_KEYS = ['gfHost', 'gfGive', 'gfKnownAmount', 'gfKnownExtraCash', 'gfHomeRoute', 'gfStartDate', 'gfRentPerWeek', 'gfRentCosts', 'gfVacancyWeeks', 'gfLegacyYears', 'gfEmergencyReserve'] as const;

const GrannyFlatAnalyser = ({ setActivePage, factFind, savedStrategies, setSavedStrategies, setFactFind, onSoaGenerated, opening = null }: GrannyFlatAnalyserProps) => {
  const hasHome = num(factFind?.home?.value) > 0;
  const homeValue = num(factFind?.home?.value);
  const todayIso = new Date().toISOString().slice(0, 10);
  const isCouple = factFind?.adviceFor === 'couple';
  /** "you" to the client, the household's names to an adviser (owner, 21 Oct 2026). */
  const voice = householdVoice(factFind);
  const goals = (factFind?.goals || {}) as Record<string, unknown>;

  /*
   * HOW THE MODULE OPENS (granny flat rebuild, 21 Oct 2026 — the village's rule). With a plan chosen, on the
   * plan page. From Home's "Start a new model", on an empty step 1 with step 3's questions asked again. Home's
   * "Open the guide" and the action plan's links open the family guide; "overview" the full guide.
   */
  const [openedFresh] = useState<boolean>(() => { try { return !!sessionStorage.getItem(GF_START_FRESH_KEY); } catch { return false; } });
  const gfChosen = useMemo(() => chosenGfPlanOf(savedStrategies as never), [savedStrategies]);
  const [gfView, setGfView] = useState<'hub' | 'steps' | 'guide' | 'overview'>(() => (
    opening === 'guide' ? 'guide' : opening === 'overview' ? 'overview' : (!openedFresh && opening !== 'calculator' && chosenGfPlanOf(savedStrategies as never) ? 'hub' : 'steps')
  ));
  /** Where the guides go back to. */
  const [guideFrom, setGuideFrom] = useState<'hub' | 'steps'>(() => (chosenGfPlanOf(savedStrategies as never) ? 'hub' : 'steps'));
  const openGuide = (view: 'guide' | 'overview') => { setGuideFrom(gfView === 'hub' ? 'hub' : 'steps'); setGfView(view); scrollAppToTop(); };
  const closeGuide = () => { setGfView(guideFrom === 'hub' && gfChosen ? 'hub' : 'steps'); scrollAppToTop(); };
  /** What the plan page asked for, done once the tested plans are back (or the hand-built plan is restored). */
  const [pendingHubAction, setPendingHubAction] = useState<'explain' | 'advice' | null>(null);

  // A fresh model: the flag cleared, and step 1's and step 3's answers taken off the file so nothing carries over.
  useEffect(() => {
    try { sessionStorage.removeItem(GF_START_FRESH_KEY); } catch { /* storage unavailable */ }
    if (openedFresh && setFactFind) {
      setFactFind((prev) => {
        const g = { ...(prev?.goals || {}) } as Record<string, unknown>;
        for (const k of GF_FRESH_GOAL_KEYS) delete g[k];
        return { ...prev, goals: g };
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /*
   * THREE STEPS (the village's spine). 1: the arrangement — who, what is given, the home, the start. 2: what it
   * means — not entering it, in four numbers. 3: the ways to set it up — the questions before the first run,
   * then the plans, or the plan built by hand.
   */
  const [step, setStep] = useState<AnalyserStep>(1);
  const [strategyMode, setStrategyMode] = useState<'manual' | 'optimised' | ''>('');
  const goToStep = (next: AnalyserStep) => {
    if (next === 3) setStrategyMode((m) => (m === '' ? 'optimised' : m));
    setStep(next);
    scrollAppToTop();
  };
  // Step 3 asks its questions before the first run, and again from "Things that change the answer".
  const [gfAsking, setGfAsking] = useState<boolean>(() => openedFresh || goals.gfLegacyYears == null);

  const startDate = String(goals.gfStartDate || '');
  // A past start date models as starting now (24 Oct 2026): the right valued at today's age and rates, the gift dated today.
  const effectiveStart = gfEffectiveStartDate(startDate);
  const [localLegacyYears, setLocalLegacyYears] = useState<number>(() => (Number(goals.gfLegacyYears) > 0 ? Number(goals.gfLegacyYears) : 10));
  const setLegacyYears = (y: number) => { setLocalLegacyYears(y); setFactFind?.((prev) => ({ ...prev, goals: { ...(prev?.goals || {}), gfLegacyYears: y } })); };

  /* ── MANUAL CONTROL'S MODEL ("Build a plan yourself") ─────────────────────────────────────── */
  const rentAnswers = useMemo(() => ({
    weekly: goals.gfRentPerWeek != null ? num(goals.gfRentPerWeek) : num(factFind?.home?.likelyRent),
    costs: goals.gfRentCosts != null ? num(goals.gfRentCosts) : num(factFind?.home?.likelyExpenses),
    vacancy: goals.gfVacancyWeeks != null ? num(goals.gfVacancyWeeks) : DEFAULT_GF_VACANCY_WEEKS,
  }), [goals.gfRentPerWeek, goals.gfRentCosts, goals.gfVacancyWeeks, factFind?.home?.likelyRent, factFind?.home?.likelyExpenses]);
  const [gfInputs, setGfInputs] = useState<GfInputs>(() => ({
    amountPaid: 0,
    transferHome: false,
    constructionCost: 0,
    fundFrom: 'savings',
    funding: {},
    fundingCgtOverrides: {},
    deployment: {},
    justMovingIn: false,
    keptHomeUse: 'vacant',
    keptHomeRent: rentAnswers.weekly,
    keptHomeExpenses: rentAnswers.costs,
    keptHomeVacancy: rentAnswers.vacancy,
  }));
  /** What is given in the hand-built plan, as the ledger shows it (kept apart from the amounts, which may be 0). */
  const [manualForm, setManualForm] = useState<GfGiveForm>('cash');
  const [manualStartedFrom, setManualStartedFrom] = useState<string | null>(null);
  /** The TESTED plan the hand-built one was customised from ("Customise this plan", 25 Oct 2026), for its record and document; null for a blank start or step 1's. */
  const [manualCustomisedFrom, setManualCustomisedFrom] = useState<string | null>(null);
  const [manualLastChange, setManualLastChange] = useState<string | null>(null);
  const [manualChosen, setManualChosen] = useState(false);
  const noteManualChange = (words: string) => { setManualLastChange(words); setManualChosen(false); };
  const setField = <K extends keyof GfInputs>(field: K, value: GfInputs[K]) => setGfInputs((prev) => ({ ...prev, [field]: value }));

  // "Just moving in" = no payment / transfer at all → no granny-flat interest, no gift, non-homeowner.
  const justMovingIn = !!gfInputs.justMovingIn;
  // The home signed over gives its value on the start date, less the loans the family member takes over (24 Oct 2026).
  const propertyTransferred = justMovingIn ? 0 : (gfInputs.transferHome ? Math.round(grannyFlatHomeGiven(factFind as never, effectiveStart || null).given) : 0);
  const amountPaid = justMovingIn ? 0 : num(gfInputs.amountPaid);
  const constructionCost = justMovingIn ? 0 : num(gfInputs.constructionCost);
  // Construction is money paid to build the accommodation: consideration given, in the homeowner test, funded from assets.
  const entryContribution = amountPaid + propertyTransferred + constructionCost;
  const cashOutlay = amountPaid + constructionCost;
  const hasArrangement = entryContribution > 0;
  const isHomeowner = entryContribution > EXTRA_ALLOWABLE_AMOUNT;
  const sellingHomeToFund = !justMovingIn && !gfInputs.transferHome && gfInputs.fundFrom === 'sell_home' && homeValue > 0 && cashOutlay > 0;
  // The home's loans are repaid from the sale (24 Oct 2026), as the engine and the server do.
  const saleResidual = sellingHomeToFund ? Math.max(0, homeValue - Math.round(homeValue * 0.025) - loansSecuredOnHome(factFind as never) - cashOutlay) : 0;
  const keepingHome = hasHome && propertyTransferred === 0 && !sellingHomeToFund;
  const shouldSimulate = hasArrangement || justMovingIn;
  const deployment = gfInputs.deployment;
  const manualIncomeOnly = sellingHomeToFund && saleResidual > 0 && deploymentIsIncomeOnly(deployment);
  const setIncomeOnlyUnderstood = incomeOnlySetter(setFactFind);
  const manualIncomeOnlyHeld = !!setIncomeOnlyUnderstood && incomeOnlyHeld(factFind, manualIncomeOnly, Number(deployment?.annuity) || 0);
  const cashFromSavings = (gfInputs.transferHome || gfInputs.fundFrom === 'savings' || !sellingHomeToFund) ? cashOutlay : 0;
  /*
   * Until the client chooses the accounts, the payment is drawn in our suggested order — the bank (keeping the
   * emergency money), then term deposits, the account-based pension and shares — the engine's own draw for the
   * tested plans (grannyFlatFactFind.buildAutoFundingMap). So the ledger's figures are live from the first change.
   */
  const fundingSuggested = cashFromSavings > 0 && Object.keys(gfInputs.funding || {}).length === 0;
  const manualReserve = num(goals.gfEmergencyReserve) > 0 ? num(goals.gfEmergencyReserve) : getRecommendedEmergencyFund(factFind);
  const funding = gfInputs.funding;
  const fundingCgtOverrides = gfInputs.fundingCgtOverrides;

  const gf = useMemo(() => ({
    amountPaid,
    propertyTransferredValue: propertyTransferred,
    constructionCost,
    dateEntered: effectiveStart,
    fundFrom: (justMovingIn || gfInputs.transferHome || !sellingHomeToFund) ? 'savings' : 'sell_home',
    deployment: sellingHomeToFund ? deployment : undefined,
    funding: cashFromSavings > 0 ? funding : undefined,
    fundingCgtOverrides,
    emergencyReserve: manualReserve,
    sellingCosts: undefined,
    // When the home is kept, how it's used drives whether the (now-assessable) former home also earns rent —
    // the rent over the weeks it is let (two empty weeks a year, suggested: the village's ruling).
    keptHomeUse: keepingHome ? gfInputs.keptHomeUse : 'vacant',
    keptHomeRent: gfRentAfterVacancy(num(gfInputs.keptHomeRent), gfInputs.keptHomeVacancy),
    keptHomeExpenses: num(gfInputs.keptHomeExpenses),
  }), [amountPaid, propertyTransferred, constructionCost, startDate, todayIso, justMovingIn, gfInputs.transferHome, sellingHomeToFund, deployment, cashFromSavings, funding, fundingCgtOverrides, manualReserve, keepingHome, gfInputs.keptHomeUse, gfInputs.keptHomeRent, gfInputs.keptHomeVacancy, gfInputs.keptHomeExpenses]);

  /* TODAY'S POSITION — step 2's four numbers and the plans' "not entering" strip. */
  const [todayFinancials, setTodayFinancials] = useState<Financials | null>(null);
  useEffect(() => {
    let cancelled = false;
    (calculationApi.calculateFinancials(factFind) as Promise<Financials>).then((r) => { if (!cancelled) setTodayFinancials(r); }).catch(() => { if (!cancelled) setTodayFinancials(null); });
    return () => { cancelled = true; };
  }, [factFind]);

  const manualOn = strategyMode === 'manual' && step === 3;
  /** The hand-built plan's figures are still being worked out (utils/calcTracker.ts, 25 Oct 2026). */
  const { track: trackGf, working: gfCalcWorking } = useCalcTracker();
  const [{ before, after: afterRaw, simulated }, setBeforeAfter] = useState<GfBeforeAfter>({ before: {}, after: {}, simulated: factFind });
  useEffect(() => {
    if (!manualOn) return undefined;
    let cancelled = false;
    void trackGf((async () => {
      let resolved: GfBeforeAfter;
      if (shouldSimulate) {
        resolved = await (calculationApi.getGrannyFlatBeforeAfter(factFind, gf) as Promise<GfBeforeAfter>);
      } else {
        const b = (await calculationApi.calculateFinancials(factFind)) as Financials;
        resolved = { before: b, after: b, simulated: factFind };
      }
      if (!cancelled) setBeforeAfter(resolved);
    })());
    return () => { cancelled = true; };
  }, [factFind, gf, shouldSimulate, manualOn]);

  /** The debt strategy, shared with aged care: applied at the origin so every figure agrees. */
  const [debtPayments, setDebtPayments] = useState<Record<string, number>>({});
  const [debtFunding, setDebtFunding] = useState<Record<string, number>>({});
  const [debtRepaymentBasis, setDebtRepaymentBasis] = useState<'keep_repayment' | 'lower_repayment'>('keep_repayment');
  const debtPayoffPlan = useMemo<DebtPayoff | null>(() => {
    const payments = Object.fromEntries(Object.entries(debtPayments).filter(([, amount]) => (Number(amount) || 0) > 0));
    if (Object.keys(payments).length === 0) return null;
    const fundingMap = Object.fromEntries(Object.entries(debtFunding).filter(([, amount]) => (Number(amount) || 0) > 0));
    return { payments, funding: fundingMap, repaymentBasis: debtRepaymentBasis };
  }, [debtPayments, debtFunding, debtRepaymentBasis]);
  /** A future start date is a REAL deferral: the engine establishes the arrangement on the day. */
  const futureGfDate = gf.dateEntered && gf.dateEntered > todayIso ? gf.dateEntered : '';

  const simulatedForModelling = useMemo(
    () => (debtPayoffPlan ? (applyDebtPayoffToFactFind(simulated as never, debtPayoffPlan).factFind as unknown as DraftFactFind) : simulated),
    [simulated, debtPayoffPlan],
  );
  const [afterWithDebt, setAfterWithDebt] = useState<Financials | null>(null);
  useEffect(() => {
    if (!debtPayoffPlan || !shouldSimulate || !manualOn) { setAfterWithDebt(null); return undefined; }
    let cancelled = false;
    trackGf(calculationApi.calculateFinancials(simulatedForModelling) as Promise<Financials>).then((r) => { if (!cancelled) setAfterWithDebt(r); });
    return () => { cancelled = true; };
  }, [simulatedForModelling, debtPayoffPlan, shouldSimulate, manualOn]);
  const after = afterWithDebt ?? afterRaw;
  const depResult = useMemo<DepResult | null>(
    () => (hasArrangement ? getGrannyFlatDeprivationFromFactFind(simulated as unknown as FactFind, PENSION_RATES.couple) : null),
    [simulated, hasArrangement],
  );
  const [projections, setProjections] = useState<ProjectionYear[]>([]);
  useEffect(() => {
    if (!shouldSimulate || !manualOn) { setProjections([]); return undefined; }
    let cancelled = false;
    const projectionsPromise = futureGfDate
      ? (calculationApi.runGrannyFlatProjections(factFind, {
            legacyYears: localLegacyYears,
            grannyFlatEvent: { date: futureGfDate, gf },
            ...(debtPayoffPlan ? { debtPayoff: debtPayoffPlan } : {}),
        }) as Promise<ProjectionYear[]>)
      : (calculationApi.runGrannyFlatProjections(simulatedForModelling, { legacyYears: localLegacyYears }) as Promise<ProjectionYear[]>);
    trackGf(projectionsPromise).then((proj) => {
      if (!cancelled) setProjections(proj);
    });
    return () => { cancelled = true; };
  }, [simulatedForModelling, shouldSimulate, localLegacyYears, futureGfDate, factFind, gf, debtPayoffPlan, manualOn]);
  const legacyEstimate = projections.length > 0
    ? (estateOf(projections[localLegacyYears - 1] ?? projections[projections.length - 1]) ?? after.netWorth)
    : after.netWorth;

  const manualDeploymentRows = describeResidualDeployment(gfInputs.deployment as ResidualDeployment);
  const reasonableValue = depResult?.reasonableValue ?? 0;
  const interestValue = depResult?.interestValue ?? 0;
  const deprivation = depResult?.deprivation ?? 0;

  /* CENTRELINK'S VALUE OF THE RIGHT AT THE START DATE, whether or not anything has been entered. */
  const [reasonableValueToday, setReasonableValueToday] = useState<number | null>(null);
  useEffect(() => {
    let cancelled = false;
    getGrannyFlatReasonableValueForClient(factFind, effectiveStart)
      .then((v) => { if (!cancelled) setReasonableValueToday(v); })
      .catch(() => { if (!cancelled) setReasonableValueToday(null); });
    return () => { cancelled = true; };
  }, [factFind, startDate, todayIso]);

  const insightFinancials = useMemo<GrannyFlatInsightFinancials>(() => ({
    agePensionBefore: before?.socialSecurity ?? 0,
    agePensionAfter: after?.socialSecurity ?? 0,
    cashflowAfter: projections[0]?.netCashflow ?? after?.cashFlow ?? 0,
    legacyEstimate,
  }), [before, after, projections, legacyEstimate]);

  /*
   * THE BENCHMARK: this client's position over the horizon if they do not enter the arrangement — step 2's
   * estate and runway, and what "See the year-by-year figures, not entering" opens. The same run the
   * engine's own baseline makes.
   */
  const [stayPutProjections, setStayPutProjections] = useState<ProjectionYear[]>([]);
  useEffect(() => {
    let cancelled = false;
    trackGf(calculationApi.runGrannyFlatProjections(factFind, { legacyYears: localLegacyYears }) as Promise<ProjectionYear[]>)
      .then((rows) => { if (!cancelled) setStayPutProjections(rows || []); })
      .catch(() => { if (!cancelled) setStayPutProjections([]); });
    return () => { cancelled = true; };
  }, [factFind, localLegacyYears]);
  const manualBaselineLegacy: number | null = useMemo(() => {
    const last = stayPutProjections[localLegacyYears - 1] ?? stayPutProjections[stayPutProjections.length - 1];
    return estateOf(last);
  }, [stayPutProjections, localLegacyYears]);
  const stayPutRunway = useMemo(
    () => (stayPutProjections.length ? planRunwayCopy(stayPutProjections as never, Number(localLegacyYears) || 10) : null),
    [stayPutProjections, localLegacyYears],
  );

  /* ── THE TESTED PLANS ─────────────────────────────────────────────────────────────────────── */
  /** The setup the engine runs on: step 1's and step 3's answers, as kept on the file (gfEngineSetupFromGoals). */
  const gfSetup = useMemo(() => gfEngineSetupFromGoals(factFind), [factFind]);
  // True until the first run is back, so step 3 never flashes "couldn't test" before the loader.
  const [seLoading, setSeLoading] = useState(true);
  const [gfSuggestions, setGfSuggestions] = useState<GrannyFlatSuggestionsResult | null>(null);
  const [seDeepDive, setSeDeepDive] = useState<{ projections: ProjectionYear[]; name?: string; id?: string } | null>(null);
  const [riskStrategy, setRiskStrategy] = useState<GfStrategyView | null>(null);
  const [explainSeId, setExplainSeId] = useState<string | null>(null);
  const [showGfExplanation, setShowGfExplanation] = useState(false);
  const [showStayPutProjections, setShowStayPutProjections] = useState(false);
  const [showAllSeStrategies, setShowAllSeStrategies] = useState(false);
  const [allSortBy, setAllSortBy] = useState<'legacy' | 'cashflow' | 'pension'>('legacy');
  const [optimisationFocus, setOptimisationFocus] = useState<GoalFocusId>('legacy');
  const [selectedSeId, setSelectedSeId] = useState<string | null>(null);
  /*
   * A real provider quote. Only the committed value re-runs the field. A quote belongs to ONE type and ONE
   * amount (annuityQuote.ts): plans of the same type use it scaled to their amount, others keep our estimate.
   */
  const [gfAnnuityQuote, setGfAnnuityQuote] = useState<AnnuityQuote | null>(null);
  const [gfAnnuityQuoteDraft, setGfAnnuityQuoteDraft] = useState<number | string>('');

  // The just-in-time "how would new money be invested?" gate — asked on the questions screen first.
  const [newMoneySkipped, setNewMoneySkipped] = useState(false);
  // The module's own questions ask this; "Not sure" there is an answer that records nothing, so once they are answered the
  // old one-question gate never shows (owner, 22 Oct 2026: it stood alone in place of the questions on a return visit).
  const needsNewMoneyAnswer = strategyMode === 'optimised' && hasHome && !hasAnsweredNewMoneyProfile(factFind) && !newMoneySkipped && !gfAsking && goals.gfLegacyYears == null;
  const answerNewMoneyProfile = (id: InvestmentProfileId) => {
    if (setFactFind) setFactFind((prev) => ({ ...prev, goals: { ...(prev.goals || {}), newMoneyInvestmentProfile: id } }));
    else setNewMoneySkipped(true);
  };
  // The Transfer Balance Cap question: only when a sale route would COMMENCE a pension and a person's balances trip the screen.
  const [tbcSkipped, setTbcSkipped] = useState(false);
  const seRunCommencesPension = useMemo(() => {
    if (!hasHome) return false;
    const room = nonConcessionalCapacityForHousehold(factFind as unknown as FactFind);
    return room.total > 0 && room.preferred == null && (gfSetup.homeRoute === 'sell' || gfSetup.homeRoute === 'all' || gfSetup.homeRoute == null);
  }, [factFind, hasHome, gfSetup.homeRoute]);
  const tbcPersonNeedingAnswer: 'client' | 'partner' | null = !tbcSkipped && seRunCommencesPension
    ? (tbcQuestionNeededFor(factFind as never, 'client') ? 'client'
      : (isCouple && tbcQuestionNeededFor(factFind as never, 'partner') ? 'partner' : null))
    : null;
  const needsTbcAnswer = strategyMode === 'optimised' && tbcPersonNeedingAnswer !== null && !gfAsking;
  const answerTbc = (amount: number) => {
    if (setFactFind && tbcPersonNeedingAnswer) {
      const key = tbcPersonNeedingAnswer;
      setFactFind((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), transferBalanceUsed: amount } }));
    } else {
      setTbcSkipped(true);
    }
  };

  // The engine runs on step 3 once the questions are answered (and never while they are open).
  useEffect(() => {
    if (strategyMode !== 'optimised' || step !== 3 || gfView !== 'steps') return undefined;
    if (gfAsking || needsNewMoneyAnswer || needsTbcAnswer) { setSeLoading(false); return undefined; }
    setSeLoading(true);
    const startedAt = Date.now();
    const t = setTimeout(async () => {
      let result: GrannyFlatSuggestionsResult | null = null;
      try { result = await buildGrannyFlatSuggestions(factFind, { legacyYears: localLegacyYears, dateEntered: effectiveStart, setup: gfSetup, annuityQuote: gfAnnuityQuote }); } catch { result = null; }
      setGfSuggestions(result);
      setTimeout(() => setSeLoading(false), Math.max(0, 1600 - (Date.now() - startedAt)));
    }, 60);
    return () => clearTimeout(t);
  // gfAnnuityQuote: a committed provider quote re-ranks the whole field (for the plans of its type).
  }, [strategyMode, step, gfView, factFind, localLegacyYears, startDate, todayIso, gfAsking, needsNewMoneyAnswer, needsTbcAnswer, gfSetup, gfAnnuityQuote]);

  /**
   * The Statement of Advice for an engine plan (gfSoa.ts). On the calendar-year setting the plan and the
   * benchmark are re-cut on calendar years first, so the document's rows match what the client reads.
   */
  const gfStrategies = useMemo(
    () => (gfSuggestions ? enrichStrategiesWithRecommendationLabels(gfSuggestions.strategies, gfSuggestions.recommended) : []) as GfStrategyView[],
    [gfSuggestions],
  );
  const gfStayPut = useMemo(() => (gfSuggestions?.stayPut as GfStrategyView | null | undefined) ?? null, [gfSuggestions]);
  const isGfBaseline = (s: GfStrategyView) => s.id === 'gf-stay-put';
  const markSoaPrepared = (planId?: string | null) => {
    if (!setSavedStrategies) return;
    setSavedStrategies((prev) => prev.map((s) => (s.type === 'granny_flat' && s.chosen === true && (planId == null || (s.inputs as { engineStrategyId?: unknown } | undefined)?.engineStrategyId === planId || s.source === 'manual')
      ? { ...s, soaPreparedAt: new Date().toISOString() } : s)));
  };
  const generateGfSoa = async (strategy: GfStrategyView) => {
    const periodMode = readPeriodMode();
    let picked = strategy;
    let all = gfStrategies;
    const rerun = gfSuggestions?.rerun;
    if (periodMode === 'calendar_year' && rerun) {
      const wanted = (rerun.candidates as { id?: unknown }[]).filter((c) => c?.id === strategy.id || c?.id === 'gf-stay-put');
      if (wanted.length) {
        try {
          const rows = (await calculationApi.evaluateGrannyFlatCandidates(factFind, wanted, rerun.legacyYears, rerun.date, 'calendar_year')) as Array<{ id?: string; projections?: ProjectionYear[] }>;
          const byId = new Map(rows.map((r) => [String(r.id), r.projections || []]));
          picked = { ...strategy, projections: byId.get(String(strategy.id)) ?? strategy.projections };
          all = gfStrategies.map((s) => (byId.has(String(s.id)) ? { ...s, projections: byId.get(String(s.id)) } : s));
        } catch { /* the financial-year rows stand */ }
      }
    }
    const snapshot = buildGrannyFlatSoaSnapshot({
      strategy: picked,
      all,
      factFind,
      legacyYears: Number(localLegacyYears) || 10,
      reasonableValue: Number(gfSuggestions?.reasonableValue) || 0,
      periodMode,
    });
    openModuleSoa(snapshot);
    markSoaPrepared(strategy.id);
    onSoaGenerated?.({ id: strategy.id, name: strategy.name, type: 'granny_flat', source: 'suggestion_engine', strategy, soaSnapshot: snapshot });
  };

  /* THE DEEP DIVE'S YEAR TOGGLE: re-cutting a candidate on the calendar year means running it again. */
  const [modalPeriodMode, setModalPeriodMode] = useState<ProjectionsModalPeriodMode>(readPeriodMode);
  const [calendarYearProjections, setCalendarYearProjections] = useState<ProjectionYear[] | null>(null);
  const deepDiveCandidate = useMemo(() => {
    const rerun = gfSuggestions?.rerun;
    if (!rerun || !seDeepDive?.id) return null;
    const candidate = (rerun.candidates as { id?: unknown }[]).find((c) => c?.id === seDeepDive.id);
    return candidate ? { candidate, legacyYears: rerun.legacyYears, date: rerun.date } : null;
  }, [gfSuggestions, seDeepDive]);
  useEffect(() => {
    if (modalPeriodMode !== 'calendar_year' || !seDeepDive || !deepDiveCandidate) { setCalendarYearProjections(null); return undefined; }
    let cancelled = false;
    setCalendarYearProjections(null);
    calculationApi.evaluateGrannyFlatCandidates(
      factFind,
      [deepDiveCandidate.candidate],
      deepDiveCandidate.legacyYears,
      deepDiveCandidate.date,
      'calendar_year',
    ).then((rows: unknown) => {
      if (cancelled) return;
      const first = Array.isArray(rows) ? (rows[0] as { projections?: ProjectionYear[] } | undefined) : undefined;
      setCalendarYearProjections(first?.projections ?? []);
    });
    return () => { cancelled = true; };
  }, [modalPeriodMode, seDeepDive, deepDiveCandidate, factFind]);

  /* GOAL LEADERS, with the aged care horizon rule: a plan whose annuity needs more years is set aside, never hidden. */
  const gfRecommended = useMemo<RecommendedStrategyEntry[]>(() => {
    if (!gfSuggestions) return [];
    const movers = gfStrategies.filter((s) => !isGfBaseline(s));
    const eligible = movers.filter((s) => gfAnnuityHorizonWarningOf(s, factFind) == null);
    /*
     * RANKED AMONG THE WAYS OF SETTING IT UP, not against not entering the arrangement (the village's rule, owner 20–21 Oct 2026; found in the
     * live review 22 Oct 2026: when every way left less than not entering the arrangement, the shared ranking withheld them all and
     * the plans screen showed no cards). The price is said in one line above the cards and an "Important"
     * line on each, against not entering the arrangement.
     */
    return buildRecommendedOptimalStrategies(eligible, null, { readings: candidateReadings });
  }, [gfSuggestions, gfStrategies, gfStayPut, factFind]);
  const gfGoalLeaders: GfStrategyView[] = useMemo(() => mergeGoalLeaderStrategies(gfRecommended) as GfStrategyView[], [gfRecommended]);
  const gfSetAside = useMemo<GfSetAside[]>(() => {
    if (!gfSuggestions) return [];
    return (mergeGoalLeaderStrategies(gfSuggestions.recommended) as GfStrategyView[])
      .map((s) => ({ strategy: s, labels: s.recommendationLabels || [], warning: gfAnnuityHorizonWarningOf(s, factFind) || '' }))
      .filter((x) => x.warning);
  }, [gfSuggestions, factFind]);
  const explainStrategy: GfStrategyView | null = useMemo(
    () => (explainSeId ? (gfStrategies.find((s) => s.id === explainSeId) || null) : null),
    [gfStrategies, explainSeId],
  );

  /** The plan chosen on an earlier visit, for its card. */
  const gfChosenEngineId = useMemo(() => {
    const id = gfChosen && gfChosen.source === 'suggestion_engine' ? gfChosen.inputs?.engineStrategyId : null;
    return typeof id === 'string' ? id : null;
  }, [gfChosen]);
  const chosenIdNow = selectedSeId ?? gfChosenEngineId;

  /*
   * INCOME ONLY NEEDS THE CLIENT'S CONFIRMATION BEFORE A TESTED PLAN IS CHOSEN (guaranteed income review E5,
   * 21 Oct 2026; IncomeOnlyTick.tsx). The full-amount-back type and a set term are never income only.
   */
  const engineIncomeOnlyHeld = (strategy: GfStrategyView | null | undefined): boolean => {
    const g = gfGuaranteedIncomeOf(strategy, factFind);
    return !!g && !!setIncomeOnlyUnderstood && incomeOnlyHeld(factFind, g.incomeOnly, g.amount);
  };

  /** The arrangement on the record, read off a plan the way the cards read it. */
  const arrangementOf = (plan: GfStrategyView, rv: number): GfPlanCardRecord['arrangement'] => {
    const m = gfMoveOf(plan, factFind);
    const given = gfGivenOf(m);
    return {
      given, cash: m.amountPaid, homeSignedOver: m.propertyTransferred, build: m.construction,
      gift: m.deprivation, interestValue: Math.max(0, given - m.deprivation), reasonableValue: rv,
      isHomeowner: m.isHomeowner, home: m.home, rentAYear: m.rented ? Math.round(m.keptHomeRent * 52) : 0,
      dateEntered: startDate, hostKind: gfHostKindOf(factFind),
    };
  };

  /**
   * Choosing a tested plan SAVES it, as aged care and the village do: the card is stored on the record, one
   * granny flat plan on record (withChosenGfPlan), and the plan page, the guide and Home read it.
   */
  const [heldSelectId, setHeldSelectId] = useState<string | null>(null);
  /*
   * THE CHOSEN PLAN'S FIGURES FOLLOW ITS RE-TEST (owner, 25 Oct 2026: as aged care and Support at home). Tested again
   * over the same years, the chosen tested plan is brought up to date on its record (Home and the plan page read it)
   * when the run has the same plan for the same arrangement (what is given); otherwise it is marked out of date.
   */
  useEffect(() => {
    if (!gfChosen || gfChosen.source !== 'suggestion_engine' || !gfSuggestions || seLoading || !setSavedStrategies) return;
    const ins = (gfChosen.inputs || {}) as { engineStrategyId?: unknown; card?: { horizonYears?: number; arrangement?: { given?: number } } };
    if (Number(ins.card?.horizonYears) !== (Number(localLegacyYears) || 10)) return;
    const row = gfStrategies.find((x) => x.id === ins.engineStrategyId);
    const sameArrangement = !!row && Math.round(arrangementOf(row, Number(gfSuggestions.reasonableValue) || 0).given) === Math.round(Number(ins.card?.arrangement?.given) || 0);
    const base = gfStrategies.find((x) => x.id === 'gf-stay-put')?.snapshot;
    const chosenId = (gfChosen as { id?: unknown }).id;
    setSavedStrategies((prev) => {
      const next = row && sameArrangement
        ? retestChosenRecord(prev as never, chosenId, { legacy: Number(row.snapshot?.legacyValue) || 0, pension: Number(row.snapshot?.pension) || 0, cashflow: Number(row.snapshot?.netCashflow) || 0 }, base ? { legacy: Number(base.legacyValue) || 0, pension: Number(base.pension) || 0, cashflow: Number(base.netCashflow) || 0 } : null, profileFingerprint(factFind))
        : markRecordOutOfDate(prev as never, chosenId, NOT_FOUND_ON_RETEST);
      return (next as typeof prev | null) ?? prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gfSuggestions, seLoading]);
  const selectEngineStrategy = (strategy: GfStrategyView) => {
    if (chosenIdNow != null && strategy.id === chosenIdNow) { setSelectedSeId(strategy.id); return; } // already chosen — no second save
    if (engineIncomeOnlyHeld(strategy)) { setHeldSelectId(strategy.id ?? null); setShowAllSeStrategies(false); return; }
    setHeldSelectId(null);
    setSelectedSeId(strategy.id ?? null);
    if (!setSavedStrategies) return;
    const recommended = gfGoalLeaders.map((l) => ({ labels: l.recommendationLabels || [], name: l.name }));
    const horizon = Number(localLegacyYears) || 10;
    const baseSnap = gfStayPut?.snapshot;
    const compare = gfComparePlanFor(strategy, gfStrategies);
    const card: GfPlanCardRecord = {
      plan: { legacy: Number(strategy.snapshot?.legacyValue) || 0, pension: Number(strategy.snapshot?.pension) || 0, cashflow: Number(strategy.snapshot?.netCashflow) || 0 },
      baseline: baseSnap ? { legacy: Number(baseSnap.legacyValue) || 0, pension: Number(baseSnap.pension) || 0, cashflow: Number(baseSnap.netCashflow) || 0 } : null,
      words: gfPlanWords(strategy, factFind),
      arrangement: arrangementOf(strategy, Number(gfSuggestions?.reasonableValue) || 0),
      horizonYears: horizon,
      riskKinds: (() => { const mv = gfMoveOf(strategy, factFind); return gfRiskKinds(String(strategy.id || ''), { construction: mv.construction, amountPaid: mv.amountPaid, home: mv.home }); })(),
      origin: 'engine',
      family: gfFamilyRecordOf(gfGuaranteedIncomeOf(strategy, factFind), factFind, strategy.projections, compare ? { rows: compare.projections, note: GF_COMPARE_NOTE } : null, horizon),
    };
    setSavedStrategies((prev) => withChosenGfPlan(prev as never, {
      id: `gf-se-${Date.now()}`,
      name: strategy.name || 'Suggested granny flat arrangement',
      displayName: card.words.title,
      type: 'granny_flat',
      source: 'suggestion_engine',
      // The client's figures it was priced on, so Home can tell when they change (chosenPlanRefresh.ts).
      inputsFingerprint: profileFingerprint(factFind),
      inputs: {
        engineStrategyId: strategy.id ?? null,
        card,
        planContext: { setup: { ...gfSetup } },
        gf: { ...(strategy.inputs || {}) },
        setup: { ...gfSetup },
        reasonableValue: Number(gfSuggestions?.reasonableValue) || 0,
        legacyYears: horizon,
        source: 'suggestion_engine',
        seStrategyName: strategy.name ?? null,
        recommendedOptimalStrategies: recommended,
        clientSelectedIsAlternative: recommended.length > 0 && !recommended.some((r) => r.name === strategy.name),
      },
      outputs: {
        agePension: strategy.snapshot?.pension ?? 0,
        cashFlow: strategy.snapshot?.netCashflow ?? 0,
        legacyEstimate: strategy.snapshot?.legacyValue ?? 0,
        deprivation: Number(strategy.deprivation) || 0,
        netWorthSchedule: buildNetWorthSchedule((strategy.projections || []) as never),
      },
      createdAt: new Date().toISOString(),
    } as never) as never);
  };

  // The explanation of an engine card is built from ITS before/after — never from the hand-built plan.
  const [explainView, setExplainView] = useState<SeView | null>(null);
  useEffect(() => {
    if (!explainStrategy) { setExplainView(null); return undefined; }
    let cancelled = false;
    const cfg: Partial<GrannyFlatConfig> = explainStrategy.inputs || {};
    (calculationApi.getGrannyFlatBeforeAfter(factFind, cfg) as Promise<GfBeforeAfter>).then(({ before: sBefore, after: sAfter, simulated: sSim }) => {
      if (cancelled) return;
      const dep = getGrannyFlatDeprivationFromFactFind(sSim as unknown as FactFind, PENSION_RATES.couple);
      const sDeploymentRows = describeResidualDeployment(cfg.deployment as ResidualDeployment | undefined);
      const proj = explainStrategy.projections || [];
      const m = gfMoveOf(explainStrategy, factFind);
      const sLegacy = proj.length
        ? (estateOf(proj[localLegacyYears - 1] ?? proj[proj.length - 1]) ?? sAfter?.netWorth ?? 0)
        : (sAfter?.netWorth ?? 0);
      setExplainView({
        name: explainStrategy.name,
        before: sBefore, after: sAfter, projections: proj, depResult: dep,
        hasArrangement: m.entryContribution > 0, justMovingIn: m.justMovingIn,
        amountPaid: m.amountPaid, propertyTransferred: m.propertyTransferred, constructionCost: m.construction, entryContribution: m.entryContribution,
        isHomeowner: m.isHomeowner,
        keepingHome: m.keeping, keptHomeRented: m.rented,
        formerHomeValue: m.keeping ? homeValue : 0,
        reasonableValue: dep?.reasonableValue ?? 0, interestValue: dep?.interestValue ?? 0, deprivation: dep?.deprivation ?? 0,
        legacyEstimate: sLegacy,
        fundingRows: explainStrategy.fundingBreakdown || [],
        fundingCgt: explainStrategy.fundingCgt || 0,
        fundingShortfall: explainStrategy.fundingShortfall || 0,
        deploymentRows: sDeploymentRows,
        saleFlow: m.selling ? { homeValue, sellingCosts: m.sellingCosts, amountPaid: m.amountPaid, residual: m.saleResidual } : null,
        insightFinancials: {
          agePensionBefore: sBefore?.socialSecurity ?? 0,
          agePensionAfter: sAfter?.socialSecurity ?? 0,
          cashflowAfter: proj[0]?.netCashflow ?? proj[0]?.cashFlow ?? sAfter?.cashFlow ?? 0,
          legacyEstimate: sLegacy,
        },
      });
    });
    return () => { cancelled = true; };
  }, [explainStrategy, factFind, homeValue, localLegacyYears]);

  /* Manual Control's model: the recap, the ledger's effect and the hand-built plan read one object. */
  const manualImpactReady = shouldSimulate;
  const fundingMeta = simulated?.__grannyFlatFundingMeta as GrannyFlatFundingMeta | undefined;
  const panelModel = useMemo<PanelModel>(() => ({
    name: null,
    before, after: manualImpactReady ? after : before, simulated, depResult,
    hasArrangement, justMovingIn,
    amountPaid, propertyTransferred, constructionCost: num(constructionCost), entryContribution, isHomeowner,
    keepingHome: keepingHome && shouldSimulate, keptHomeRented: keepingHome && gfInputs.keptHomeUse === 'rented',
    formerHomeValue: keepingHome ? homeValue : 0,
    reasonableValue, interestValue, deprivation,
    legacyEstimate: manualImpactReady ? legacyEstimate : (before?.netWorth ?? 0),
    projections: manualImpactReady ? projections : [],
    fundingRows: manualImpactReady ? (fundingMeta?.breakdown || []) : [],
    fundingCgt: manualImpactReady ? (fundingMeta?.cgt || 0) : 0,
    fundingShortfall: manualImpactReady ? (fundingMeta?.shortfall || 0) : 0,
    deploymentRows: manualImpactReady ? manualDeploymentRows : [],
    saleFlow: (manualImpactReady && sellingHomeToFund) ? { homeValue, sellingCosts: Math.round(homeValue * 0.025), amountPaid: cashOutlay, residual: saleResidual } : null,
    insightFinancials: manualImpactReady ? insightFinancials : null,
  }), [before, after, manualImpactReady, simulated, depResult, hasArrangement, justMovingIn, amountPaid, propertyTransferred, entryContribution, isHomeowner, keepingHome, shouldSimulate, gfInputs.keptHomeUse, homeValue, reasonableValue, interestValue, deprivation, legacyEstimate, projections, fundingMeta, manualDeploymentRows, sellingHomeToFund, cashOutlay, saleResidual, insightFinancials]);

  /*
   * ── MANUAL CONTROL'S PLAN, AS A PLAN. The hand-built arrangement in the engine card's shape, so the choice
   * goes through the same record and the document is written by the same buildGrannyFlatSoaSnapshot. Its id
   * is composed from what the plan does so the risks and the document's parts are read the engine's way.
   */
  const manualPlanId = useMemo(() => {
    let id = justMovingIn ? 'gf-move-in-only' : propertyTransferred > 0 ? 'gf-transfer-home' : sellingHomeToFund ? 'gf-sell' : 'gf-known';
    if (!justMovingIn && keepingHome && gfInputs.keptHomeUse === 'rented') id += '-keep-rent';
    if (sellingHomeToFund) {
      const d = deployment as ProceedsDeploymentValue;
      const terms = deploymentAnnuityTerms(d, isCouple);
      const amt = (v: unknown) => parseFloat(String(v ?? '')) || 0;
      if (terms && amt(terms.purchaseAmount) > 0) id += d.annuityType === 'guaranteed' ? '-annuity-guaranteed' : amt(terms.residualPercent) > 0 ? '-annuity-rcv' : '-annuity';
      else if (amt(d.investmentProperty) > 0) id += '-property';
      else if (amt(d.superContribution) + amt(d.partnerSuperContribution) > 0) id += '-super';
      else if (amt(d.investmentBond) > 0) id += '-bond';
      else if (amt(d.shares) > 0) id += '-shares';
    }
    if (debtPayoffPlan) id += '-clear-debt';
    return `${id}`;
  }, [justMovingIn, propertyTransferred, sellingHomeToFund, keepingHome, gfInputs.keptHomeUse, deployment, isCouple, debtPayoffPlan]);
  const manualPlanView = useMemo<GfStrategyView | null>(() => {
    if (!(strategyMode === 'manual' && manualImpactReady)) return null;
    return {
      id: manualPlanId,
      name: 'Your granny flat arrangement',
      description: 'The arrangement as built by hand.',
      inputs: { ...gf } as Partial<GrannyFlatConfig>,
      snapshot: {
        pension: Number(panelModel.after?.socialSecurity) || 0,
        netCashflow: Number(panelModel.projections[0]?.netCashflow ?? panelModel.after?.cashFlow) || 0,
        legacyValue: Number(panelModel.legacyEstimate) || 0,
      },
      projections: panelModel.projections,
      fundingBreakdown: panelModel.fundingRows,
      fundingCgt: panelModel.fundingCgt,
      fundingShortfall: panelModel.fundingShortfall,
      deprivation: panelModel.deprivation,
    } as GfStrategyView;
  }, [strategyMode, manualImpactReady, manualPlanId, gf, panelModel]);
  /** Not entering, in the engine's shape — the benchmark the manual plan and its document are measured against. */
  const manualStayPutView = useMemo<GfStrategyView>(() => ({
    id: 'gf-stay-put',
    name: 'Do not enter the arrangement',
    snapshot: {
      pension: Number(todayFinancials?.socialSecurity ?? before?.socialSecurity) || 0,
      netCashflow: Number(stayPutProjections[0]?.netCashflow ?? todayFinancials?.cashFlow ?? before?.cashFlow) || 0,
      legacyValue: Number(manualBaselineLegacy) || 0,
    },
    projections: stayPutProjections,
  } as GfStrategyView), [todayFinancials, before, stayPutProjections, manualBaselineLegacy]);
  /** The hand-built plan's guaranteed income (only when the home is sold and part of it funds one). */
  const manualGuaranteed = useMemo(() => (manualPlanView ? gfGuaranteedIncomeOf(manualPlanView, factFind) : null), [manualPlanView, factFind]);

  /** Choosing the hand-built plan: the same card record a tested plan saves (Manual Control parity). */
  const chooseManualPlan = () => {
    if (!manualPlanView || manualIncomeOnlyHeld || panelModel.fundingShortfall >= 1) return;
    const horizon = Number(localLegacyYears) || 10;
    const words = gfPlanWords(manualPlanView, factFind);
    const card: GfPlanCardRecord = {
      plan: { legacy: Number(manualPlanView.snapshot?.legacyValue) || 0, pension: Number(manualPlanView.snapshot?.pension) || 0, cashflow: Number(manualPlanView.snapshot?.netCashflow) || 0 },
      baseline: { legacy: Number(manualStayPutView.snapshot?.legacyValue) || 0, pension: Number(manualStayPutView.snapshot?.pension) || 0, cashflow: Number(manualStayPutView.snapshot?.netCashflow) || 0 },
      words,
      arrangement: arrangementOf(manualPlanView, Number(reasonableValue || reasonableValueToday) || 0),
      horizonYears: horizon,
      riskKinds: (() => { const mv = gfMoveOf(manualPlanView, factFind); return gfRiskKinds(manualPlanId, { construction: mv.construction, amountPaid: mv.amountPaid, home: mv.home }); })(),
      origin: 'manual',
      family: gfFamilyRecordOf(manualGuaranteed, factFind, panelModel.projections, null, horizon),
    };
    const id = `gf-manual-${Date.now()}`;
    setManualChosen(true);
    if (!setSavedStrategies) return;
    setSavedStrategies((prev) => withChosenGfPlan(prev as never, {
      id,
      name: words.title,
      displayName: words.title,
      type: 'granny_flat',
      source: 'manual',
      inputsFingerprint: profileFingerprint(factFind),
      // Built by the client from a tested plan ("Customise this plan", 25 Oct 2026).
      ...(manualCustomisedFrom ? { customisedFrom: manualCustomisedFrom } : {}),
      inputs: {
        card,
        planContext: {},
        gf: { ...(manualPlanView.inputs || {}) },
        manualInputs: { ...gfInputs },
        manualForm,
        ...(debtPayoffPlan ? { debtPayoff: debtPayoffPlan } : {}),
        reasonableValue: Number(reasonableValue || reasonableValueToday) || 0,
        legacyYears: horizon,
        source: 'manual',
      },
      outputs: {
        agePension: card.plan.pension,
        cashFlow: card.plan.cashflow,
        legacyEstimate: card.plan.legacy,
        deprivation: panelModel.deprivation,
        netWorthSchedule: buildNetWorthSchedule((manualPlanView.projections || []) as never),
      },
      createdAt: new Date().toISOString(),
    } as never) as never);
  };
  /** The advice document for the hand-built plan — the engine path's builder, the same register. */
  const generateManualSoa = () => {
    if (!manualPlanView) return;
    const snapshot = buildGrannyFlatSoaSnapshot({
      strategy: manualPlanView,
      all: [manualPlanView, manualStayPutView],
      factFind,
      legacyYears: Number(localLegacyYears) || 10,
      reasonableValue: Number(reasonableValue || reasonableValueToday) || 0,
      // The rows on screen are the financial-year run, so no calendar-year label is put on them.
      // A hand-built plan's document says so (25 Oct 2026: it was labelled as a tested plan), and where it began.
      manual: true,
      customisedFrom: manualCustomisedFrom,
    });
    openModuleSoa(snapshot);
    markSoaPrepared(null);
    onSoaGenerated?.({ id: manualPlanView.id, name: manualPlanView.name, type: 'granny_flat', source: 'manual', strategy: manualPlanView, soaSnapshot: snapshot });
  };

  /** Seed the ledger from a tested plan's inputs ("Customise this plan", "Start from"). */
  const seedManualFrom = (row: GfStrategyView) => {
    const cfg = (row.inputs || {}) as Partial<GrannyFlatConfig>;
    const move = gfMoveOf(row, factFind);
    const form: GfGiveForm = move.justMovingIn ? 'nothing' : move.propertyTransferred > 0 ? 'home' : move.construction > 0 ? 'build' : 'cash';
    setManualForm(form);
    setGfInputs((prev) => ({
      ...prev,
      justMovingIn: form === 'nothing',
      transferHome: form === 'home',
      amountPaid: num(cfg.amountPaid),
      constructionCost: num(cfg.constructionCost),
      fundFrom: cfg.fundFrom === 'sell_home' ? 'sell_home' : 'savings',
      funding: (cfg.funding as Record<string, number> | undefined) || {},
      fundingCgtOverrides: {},
      deployment: ((cfg.deployment as ProceedsDeploymentValue | undefined) || {}),
      keptHomeUse: cfg.keptHomeUse === 'rented' ? 'rented' : 'vacant',
      keptHomeRent: rentAnswers.weekly,
      keptHomeExpenses: cfg.keptHomeUse === 'rented' && cfg.keptHomeExpenses != null ? num(cfg.keptHomeExpenses) : rentAnswers.costs,
      keptHomeVacancy: rentAnswers.vacancy,
    }));
    // A plan that also clears the debts brings its payoff with it.
    const cand = (gfSuggestions?.rerun.candidates as Array<{ id?: unknown; debtPayoff?: DebtPayoff }> | undefined)?.find((c) => c?.id === row.id);
    setDebtPayments(cand?.debtPayoff?.payments ? { ...cand.debtPayoff.payments } : {});
    setDebtFunding({ ...((cand?.debtPayoff?.funding || {}) as Record<string, number>) });
    setDebtRepaymentBasis(cand?.debtPayoff?.repaymentBasis === 'lower_repayment' ? 'lower_repayment' : 'keep_repayment');
    setManualStartedFrom(gfPlanWords(row, factFind).title);
    setManualCustomisedFrom(gfPlanWords(row, factFind).title);
    setManualLastChange(null);
    setManualChosen(false);
  };
  /** Restore a hand-built plan from its record (the plan page's "Build a plan yourself"). */
  const restoreManualFrom = (rec: ChosenGfPlan) => {
    const ins = rec.inputs as { manualInputs?: Partial<GfInputs>; manualForm?: GfGiveForm; debtPayoff?: DebtPayoff } | undefined;
    if (ins?.manualInputs) setGfInputs((prev) => ({ ...prev, ...ins.manualInputs }));
    if (ins?.manualForm) setManualForm(ins.manualForm);
    setDebtPayments(ins?.debtPayoff?.payments ? { ...ins.debtPayoff.payments } : {});
    setDebtFunding({ ...((ins?.debtPayoff?.funding || {}) as Record<string, number>) });
    setDebtRepaymentBasis(ins?.debtPayoff?.repaymentBasis === 'lower_repayment' ? 'lower_repayment' : 'keep_repayment');
    // Its own record says where it began, if it was customised from a tested plan.
    const from = (rec as { customisedFrom?: string }).customisedFrom ?? null;
    setManualStartedFrom(from ?? rec.inputs?.card?.words.title ?? null);
    setManualCustomisedFrom(from);
    setManualChosen(true);
  };
  /*
   * BUILD A PLAN YOURSELF STARTS FROM STEP 1 (owner, 24 Oct 2026: a $600,000 build chosen on step 1 opened here as
   * "pay nothing" with a $0 build). What they said they would give, its amounts, and what happens to the home.
   * Returns false when step 1 is still "not sure", so the caller keeps its own starting point.
   */
  const seedManualFromStep1 = (): boolean => {
    const give = gfGiveOf(factFind);
    if (give === 'unsure') return false;
    const known = num(goals.gfKnownAmount);
    const extra = num(goals.gfKnownExtraCash);
    const route = goals.gfHomeRoute;
    const form: GfGiveForm = give === 'home' ? 'home' : give === 'build' ? 'build' : 'cash';
    setManualForm(form);
    setGfInputs((prev) => ({
      ...prev,
      justMovingIn: false,
      transferHome: form === 'home',
      amountPaid: form === 'cash' ? (known > 0 ? known : Math.floor(Number(reasonableValueToday) || 0)) : extra,
      constructionCost: form === 'build' ? known : 0,
      fundFrom: form !== 'home' && route === 'sell' ? 'sell_home' : 'savings',
      funding: {},
      fundingCgtOverrides: {},
      deployment: {},
      keptHomeUse: route === 'keep' ? 'rented' : 'vacant',
      keptHomeRent: rentAnswers.weekly,
      keptHomeExpenses: rentAnswers.costs,
      keptHomeVacancy: rentAnswers.vacancy,
    }));
    setDebtPayments({});
    setDebtFunding({});
    setDebtRepaymentBasis('keep_repayment');
    setManualStartedFrom('What you described on step 1');
    setManualCustomisedFrom(null);
    setManualLastChange(null);
    setManualChosen(false);
    return true;
  };
  /*
   * EACH WAY OF PAYING KEEPS ITS OWN AMOUNTS here too (24 Oct 2026: switching from cash to "sign the home over" kept
   * $398,316 as cash on top of the home, a $398,316 gift). Switching puts away what was entered for the way left.
   */
  const manualStash = useRef<Partial<Record<GfGiveForm, { amountPaid: number; constructionCost: number }>>>({});
  const startBlankManualPlan = () => {
    setManualForm('cash');
    setGfInputs((prev) => ({ ...prev, justMovingIn: false, transferHome: false, amountPaid: 0, constructionCost: 0, fundFrom: 'savings', funding: {}, fundingCgtOverrides: {}, deployment: {}, keptHomeUse: 'vacant' }));
    setDebtPayments({});
    setDebtFunding({});
    setDebtRepaymentBasis('keep_repayment');
    setManualStartedFrom('A blank plan');
    setManualCustomisedFrom(null);
    setManualLastChange(null);
    setManualChosen(false);
  };
  /**
   * "Build a plan yourself" (owner, 25 Oct 2026): a blank hand-built plan, which for the granny flat is what step 1
   * decided (24 Oct 2026: a $600,000 build opened as "pay nothing") and nothing else; where step 1 is "not sure",
   * a blank cash plan. It used to keep whatever the ledger last held.
   */
  const openGfBlank = () => {
    if (!seedManualFromStep1()) startBlankManualPlan();
  };

  /* "Compare with the strongest option" — on demand, information, never a switch. */
  const [manualComparison, setManualComparison] = useState<{ loading: boolean; best: GfStrategyView | null } | null>(null);
  const runManualComparison = async () => {
    const fromField = gfStrategies.filter((s) => !isGfBaseline(s));
    if (fromField.length) {
      setManualComparison({ loading: false, best: fromField.reduce((top, s) => (num(s.snapshot?.legacyValue) > num(top.snapshot?.legacyValue) ? s : top), fromField[0]!) });
      return;
    }
    setManualComparison({ loading: true, best: null });
    try {
      const result = await buildGrannyFlatSuggestions(factFind, { legacyYears: localLegacyYears, dateEntered: effectiveStart, setup: gfSetup });
      const movers = ((result?.strategies || []) as GfStrategyView[]).filter((s) => s.id !== 'gf-stay-put');
      setManualComparison({ loading: false, best: movers.length ? movers.reduce((top, s) => (num(s.snapshot?.legacyValue) > num(top.snapshot?.legacyValue) ? s : top), movers[0]!) : null });
    } catch {
      setManualComparison({ loading: false, best: null });
    }
  };
  useEffect(() => { setManualComparison(null); }, [gfInputs, localLegacyYears]);

  /* The plan page's Explain this / the advice document, done once the plans (or the hand-built plan) are back. */
  useEffect(() => {
    if (!pendingHubAction) return;
    if (gfChosen?.source === 'manual') {
      if (!manualPlanView || !panelModel.projections.length) return;
      setPendingHubAction(null);
      if (pendingHubAction === 'explain') { setExplainSeId(null); setShowGfExplanation(true); } else generateManualSoa();
      return;
    }
    if (seLoading || !gfSuggestions) return;
    const row = gfStrategies.find((x) => x.id === chosenIdNow);
    setPendingHubAction(null);
    if (!row) return;
    if (pendingHubAction === 'explain') { setExplainSeId(row.id ?? null); setShowGfExplanation(true); } else void generateGfSoa(row);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingHubAction, seLoading, gfSuggestions, manualPlanView, panelModel.projections.length]);

  const openSteps = (to: AnalyserStep, mode: 'optimised' | 'manual' = 'optimised') => { setGfView('steps'); setStrategyMode(mode); goToStep(to); };
  /*
   * A NEW MODEL FROM SCRATCH, FROM THE PLAN PAGE (owner, 22 Oct 2026: every plan page has aged care's "Model
   * something else"). Home's "Start a new model" does this on opening; here it is done in place: step 1's and
   * step 3's answers come off the file, step 3 asks its questions again, and the module opens on an empty step 1.
   * The chosen plan stays chosen until another is chosen.
   */
  const startFresh = () => {
    setFactFind?.((prev) => {
      const g = { ...(prev?.goals || {}) } as Record<string, unknown>;
      for (const k of GF_FRESH_GOAL_KEYS) delete g[k];
      return { ...prev, goals: g };
    });
    setLocalLegacyYears(10);
    setGfAsking(true);
    setManualStartedFrom(null);
    setManualChosen(false);
    setGfView('steps');
    setStrategyMode('');
    setStep(1);
    scrollAppToTop();
  };
  const openChosenForAction = (action: 'explain' | 'advice') => {
    setPendingHubAction(action);
    if (gfChosen?.source === 'manual') { restoreManualFrom(gfChosen); openSteps(3, 'manual'); } else openSteps(3, 'optimised');
  };

  // Escape closes "every way tested".
  useEffect(() => {
    if (!showAllSeStrategies) return undefined;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setShowAllSeStrategies(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showAllSeStrategies]);

  /** The risks of a plan: its parts, and the fairness point where the file records two or more children (and the host is a child). */
  const risksFor = (kinds: ReturnType<typeof riskKindsFromCandidateId>, move: { propertyTransferred: number; entryContribution: number }, horizonWarning: string | null, stampDuty: number | null) => buildStrategyRisks(kinds, {
    familyToKeepWhole: gfFamilyToKeepWhole(factFind, move),
    horizonWarning,
    stampDuty,
  });

  /** The family guide's figures: the chosen plan's, from its record. */
  const guideFigures = useMemo(() => gfGuideFiguresOf(gfChosen), [gfChosen]);
  const printBriefFromHub = () => {
    const answers = factFind?.grannyFlat?.familyGuide || {};
    const generatedAt = new Date().toISOString();
    openGfSolicitorBrief({ clientName: factFind?.client?.firstName || 'you', partnerName: isCouple ? (factFind?.partner?.firstName || null) : null, answers, figures: guideFigures, generatedAt });
    const brief = guideFigures
      ? { generatedAt, entryContribution: guideFigures.entryContribution, interestValue: guideFigures.interestValue, deprivation: guideFigures.deprivation, isHomeowner: guideFigures.isHomeowner, dateEntered: guideFigures.dateEntered, ...(guideFigures.planTitle ? { planTitle: guideFigures.planTitle } : {}) }
      : { generatedAt };
    setFactFind?.((prev) => ({ ...prev, grannyFlat: { ...(prev.grannyFlat || {}), familyGuide: { ...(prev.grannyFlat?.familyGuide || {}), brief } } }));
  };

  /* ── THE VIEWS ─────────────────────────────────────────────────────────────────────────────── */
  if (gfView === 'guide') {
    return (
      <GfFamilyGuide
        factFind={factFind}
        setFactFind={setFactFind}
        figures={guideFigures}
        planForm={gfGuideFormOf((gfChosen?.inputs as { card?: { arrangement?: { cash?: number; build?: number; homeSignedOver?: number } } } | undefined)?.card?.arrangement)}
        onBack={closeGuide}
        backLabel={guideFrom === 'hub' && gfChosen ? 'Back to the plan' : 'Back to the granny flat'}
        onToPlans={() => openSteps(3)}
        onHome={() => setActivePage('Dashboard')}
      />
    );
  }
  if (gfView === 'overview') {
    return <GrannyFlatExplained onBack={closeGuide} onFamilyGuide={() => setGfView('guide')} factFind={factFind} />;
  }
  if (gfView === 'hub' && gfChosen) {
    const card = gfChosen.inputs!.card!;
    return (
      <div className="max-w-[1240px] mx-auto">
        <GfHub
          factFind={factFind}
          outOfDate={chosenPlanOutOfDate(gfChosen, factFind)}
          plan={{ ...gfChosen, soaSnapshot: gfChosen.soaSnapshot ?? (gfChosen as { soaPreparedAt?: string }).soaPreparedAt }}
          onChangeArrangement={() => openSteps(1)}
          onModelAnother={() => openSteps(1)}
          onStartFresh={startFresh}
          onAllPlans={() => openSteps(3)}
          onExplain={() => openChosenForAction('explain')}
          onRisks={() => setRiskStrategy({ id: String(gfChosen.inputs?.engineStrategyId || ''), name: card.words.title, __kinds: card.riskKinds, __move: { propertyTransferred: card.arrangement.homeSignedOver, entryContribution: card.arrangement.given } } as unknown as GfStrategyView)}
          onAdvice={() => openChosenForAction('advice')}
          onBuild={() => {
            if (gfChosen.source === 'manual') restoreManualFrom(gfChosen);
            else {
              const ins = gfChosen.inputs as { gf?: Partial<GrannyFlatConfig>; engineStrategyId?: string } | undefined;
              seedManualFrom({ id: String(ins?.engineStrategyId || ''), inputs: ins?.gf || {}, fundingBreakdown: [], deprivation: card.arrangement.gift } as unknown as GfStrategyView);
              setManualStartedFrom(card.words.title);
              setManualCustomisedFrom(card.words.title);
            }
            openSteps(3, 'manual');
          }}
          onBuildNew={() => { openGfBlank(); openSteps(3, 'manual'); }}
          onFamilyGuide={() => openGuide('guide')}
          onPrintBrief={printBriefFromHub}
          onReadGuide={() => openGuide('overview')}
          onAssumptions={() => setActivePage('AssumptionsPage')}
          onHome={() => setActivePage('Dashboard')}
        />
        {riskStrategy && (() => {
          /*
           * THE SAME SPECIFICS AS THE TESTED PLAN'S LIST (owner, 22 Oct 2026: one "What could go wrong" per plan, wherever
           * it is opened). The saved record keeps the plan's own settings (inputs.gf, engine or hand-built), so the
           * guaranteed income's horizon warning and the stamp duty on a bought property are read from those, not dropped.
           */
          const savedGf = ((gfChosen.inputs as { gf?: Partial<GrannyFlatConfig> } | undefined)?.gf) || {};
          const savedAsPlan = { id: String(gfChosen.inputs?.engineStrategyId || ''), inputs: savedGf } as unknown as GfStrategyView;
          return (
            <StrategyRisksPanel
              voice={voice}
              strategyName={card.words.title}
              onClose={() => setRiskStrategy(null)}
              risks={risksFor(card.riskKinds || [], { propertyTransferred: card.arrangement.homeSignedOver, entryContribution: card.arrangement.given }, gfAnnuityHorizonWarningOf(savedAsPlan, factFind), Number((savedGf.deployment as { ipStampDuty?: number } | undefined)?.ipStampDuty) || null)}
            />
          );
        })()}
      </div>
    );
  }

  const stepDefs = [
    { title: 'The arrangement', sub: 'Who, what is given, the home' },
    { title: `What it means for ${voice.them}`, sub: 'Staying put, in four numbers' },
    { title: 'Ways to set it up', sub: 'Plans we tested, or build your own' },
  ] as const;
  const crumb = step === 1 ? 'The arrangement' : step === 2 ? 'What it means' : strategyMode === 'manual' ? 'Build a plan yourself' : 'Ways to set it up';
  const host = gfHostPhrase(factFind);

  /** The plans' detail layer: where the money comes from, the alternative, a quote for a guaranteed income. */
  const renderDetail = (row: GfStrategyView) => {
    const terms = deploymentAnnuityTerms(row.inputs?.deployment as ProceedsDeploymentValue | undefined, isCouple);
    const guaranteed = gfGuaranteedIncomeOf(row, factFind);
    const moneyRows = [
      ...(row.fundingBreakdown || []),
      ...describeResidualDeployment(row.inputs?.deployment as ResidualDeployment | undefined),
      ...((row.fundingCgt ?? 0) > 0 ? [{ label: 'Capital gains tax on the shares sold', amount: row.fundingCgt ?? 0 }] : []),
    ];
    const alt = alternativeAssetNote(row, gfStrategies, (s) => s.snapshot?.legacyValue ?? null, Number(localLegacyYears) || 10);
    return (
      <div className="space-y-3 text-[13.5px] text-slate-700" data-testid="gf-card-detail">
        {moneyRows.length > 0 && (
          <div>
            <p className="font-semibold text-slate-900">How it is paid for</p>
            <table className="mt-1 w-full tabular-nums"><tbody>
              {moneyRows.map((r, i) => <tr key={i} className="border-b border-slate-100"><td className="py-1">{r.label}</td><td className="py-1 text-right">{formatCurrency(Math.round(Number(r.amount) || 0))}</td></tr>)}
            </tbody></table>
          </div>
        )}
        {row.description && <p className="text-slate-600">{row.description}</p>}
        {alt && <p data-testid="gf-alternative"><strong className="text-slate-900">{alt.question}</strong> {alt.answer}</p>}
        {terms && (
          <EngineAnnuityTerms
            terms={terms}
            factFind={factFind}
            quote={<>
              <AnnuityQuoteControl
                draft={gfAnnuityQuoteDraft}
                onDraft={setGfAnnuityQuoteDraft}
                committed={guaranteed ? (quotedIncomeFor(gfAnnuityQuote, guaranteed.annuityType, guaranteed.residualPercent, guaranteed.amount) ?? null) : null}
                onCommit={(v) => setGfAnnuityQuote(v != null && v > 0 && guaranteed
                  ? { income: v, amount: guaranteed.amount, shape: annuityQuoteShapeOf(guaranteed.annuityType, guaranteed.residualPercent) }
                  : null)}
              />
              {gfAnnuityQuote && <p className="mt-2 text-[12.5px] text-slate-600" data-testid="gf-annuity-quote-note">{annuityQuoteNote(gfAnnuityQuote, formatCurrency)}</p>}
            </>}
          />
        )}
        <button type="button" onClick={() => setSeDeepDive({ projections: row.projections || [], name: gfPlanWords(row, factFind).title, id: row.id })} className="font-semibold text-brand-navy underline decoration-slate-300 underline-offset-4">See the year-by-year figures</button>
      </div>
    );
  };

  const homeUse: GfHomeUse = sellingHomeToFund ? 'sold' : gfInputs.keptHomeUse;
  const ownToday = Number(todayFinancials?.netWorth) || 0;

  return (
    <div className="max-w-[1320px] mx-auto">
      <p className="text-[13px] text-slate-500">
        <button type="button" onClick={() => setActivePage('Dashboard')} className="underline decoration-dotted underline-offset-2 hover:text-slate-800">Home</button>
        {' › Granny flat › '}{crumb}
      </p>
      <h1 className="mt-2 text-[32px] font-bold leading-tight tracking-tight text-brand-navy" data-testid="gf-title">Living with family: a granny flat for {voice.names}</h1>
      {step === 1 && <p className="mt-1 text-[15px] text-slate-600">Three short steps: the arrangement, what it would mean for {voice.them}, and the ways to set it up.</p>}
      {gfChosen && (
        <p className="mt-2 text-[14px] text-slate-600" data-testid="gf-chosen-note">
          {voice.Possessive} chosen plan: <strong className="text-slate-900">{gfChosen.inputs?.card?.words.title}</strong>.{' '}
          <button type="button" onClick={() => { setGfView('hub'); scrollAppToTop(); }} className="font-semibold text-brand-navy underline decoration-slate-300 underline-offset-4" data-testid="gf-go-to-plan-page">Go to {voice.possessive} granny flat plan</button>
        </p>
      )}
      <AnalyserSteps step={step} onStep={goToStep} steps={stepDefs} ariaLabel="Granny flat steps" />

      {step === 1 && (
        <div className="mt-8">
          <GfStep1
            factFind={factFind}
            setFactFind={setFactFind}
            reasonableValue={reasonableValueToday}
            onNext={() => goToStep(2)}
            onReadGuide={() => openGuide('overview')}
            onFamilyGuide={() => openGuide('guide')}
          />
        </div>
      )}

      {step === 2 && (
        <div className="mt-8">
          <GfStep2
            factFind={factFind}
            todayFinancials={todayFinancials as { socialSecurity?: number; cashFlow?: number; netWorth?: number; socialSecurityBreakdown?: unknown } | null}
            stayPutLegacy={manualBaselineLegacy}
            runway={stayPutRunway}
            legacyYears={localLegacyYears}
            reasonableValue={reasonableValueToday}
            onLegacyYearsChange={setLegacyYears}
            onBack={() => goToStep(1)}
            onNext={() => goToStep(3)}
            onReadGuide={() => openGuide('overview')}
            onAssumptions={() => setActivePage('AssumptionsPage')}
            onShowProjections={() => setShowStayPutProjections(true)}
          />
        </div>
      )}

      {step === 3 && strategyMode !== 'manual' && (
        <div className="mt-8">
          {gfAsking ? (
            <GfQuestions
              factFind={factFind}
              reasonableValue={reasonableValueToday}
              firstRun={goals.gfLegacyYears == null || openedFresh}
              onRun={(answers) => {
                const add = gfAnswersToGoals(answers);
                setFactFind?.((prev) => ({ ...prev, goals: { ...(prev?.goals || {}), ...add } }));
                setLocalLegacyYears(answers.years);
                setGfInputs((prev) => ({ ...prev, keptHomeRent: answers.rentPerWeek, keptHomeExpenses: answers.rentCosts, keptHomeVacancy: answers.vacancyWeeks }));
                setNewMoneySkipped(true);
                setGfAsking(false);
                setStrategyMode('optimised');
                scrollAppToTop();
              }}
              onBuildYourOwn={() => { openGfBlank(); setGfAsking(false); setStrategyMode('manual'); scrollAppToTop(); }}
              onFamilyGuide={() => openGuide('guide')}
              onCancel={goals.gfLegacyYears == null ? undefined : () => setGfAsking(false)}
            />
          ) : needsNewMoneyAnswer ? (
            <Card><NewMoneyProfileGate onAnswer={answerNewMoneyProfile} onSkip={() => setNewMoneySkipped(true)} contextLine="Some of the plans invest the money a sale of the home frees." /></Card>
          ) : needsTbcAnswer ? (
            <Card>
              <TransferCapGate
                name={(tbcPersonNeedingAnswer === 'partner' ? factFind?.partner?.firstName : factFind?.client?.firstName) || 'this person'}
                onAnswer={answerTbc}
                onSkip={() => setTbcSkipped(true)}
              />
            </Card>
          ) : seLoading ? (
            <SuggestionEngineAnalysisLoader
              title="Testing the ways to set it up"
              subtitle="We're modelling each way of paying for the right and handling the home, and staying put, over the same years."
            />
          ) : !gfSuggestions || gfStrategies.length === 0 ? (
            <p className="text-[14px] text-slate-600">We couldn&apos;t test the ways to set it up for this arrangement. <button type="button" className="font-semibold text-brand-navy underline" onClick={() => setStrategyMode('manual')}>Build a plan yourself</button> instead.</p>
          ) : (
            <GfPlansView
              notices={(() => {
                const chosenHere = gfChosen?.inputs?.card && gfStrategies.some((x) => x.id === chosenIdNow);
                if (chosenHere && gfChosen?.inputs?.card) {
                  return (
                    <div className="flex flex-wrap items-center gap-4 rounded-2xl border border-slate-200 border-l-4 border-l-emerald-500 bg-white px-5 py-4 shadow-card" data-testid="gf-chosen-banner">
                      <span className="text-[26px] leading-none text-emerald-500" aria-hidden>✓</span>
                      <p className="min-w-0 flex-1 text-[14px] leading-relaxed text-slate-700">
                        <strong className="text-slate-900">{voice.you ? "You've" : `${voice.Names} have`} chosen &ldquo;{gfChosen.inputs.card.words.title}&rdquo;.</strong>{' '}
                        It&apos;s saved with {voice.possessive} file. Next, the family discussion guide, then a solicitor to put the agreement in writing.
                      </p>
                      <button type="button" onClick={() => { setGfView('hub'); scrollAppToTop(); }} className="text-[14px] font-semibold text-brand-navy underline decoration-slate-300 underline-offset-4" data-testid="gf-banner-plan-page">Go to the granny flat plan</button>
                    </div>
                  );
                }
                // A plan chosen from "every way tested" that needs the income-only confirmation first.
                const held = heldSelectId ? gfStrategies.find((s) => s.id === heldSelectId) : null;
                const g = held ? gfGuaranteedIncomeOf(held, factFind) : null;
                if (held && g && setIncomeOnlyUnderstood) {
                  return (
                    <div className="space-y-2 rounded-2xl border border-slate-200 bg-white p-5 shadow-card" data-testid="gf-held-select">
                      <p className="text-[14px] text-slate-700"><strong className="text-slate-900">{gfPlanWords(held, factFind).title}</strong> has a guaranteed income where nothing comes back.</p>
                      <IncomeOnlyBeforeChoose factFind={factFind} amount={g.amount} onChange={setIncomeOnlyUnderstood} />
                      <Button variant="primary" size="sm" onClick={() => selectEngineStrategy(held)} disabled={engineIncomeOnlyHeld(held)} data-testid="gf-held-select-button">Choose this plan</Button>
                    </div>
                  );
                }
                return null;
              })()}
              factFind={factFind}
              strategies={gfStrategies}
              stayPut={gfStayPut}
              recommended={gfRecommended}
              setAside={gfSetAside}
              horizonYears={Number(localLegacyYears) || 10}
              ownToday={ownToday}
              focus={optimisationFocus}
              onFocus={setOptimisationFocus}
              chosenId={chosenIdNow}
              onChoose={selectEngineStrategy}
              onExplain={(row) => { setExplainSeId(row.id ?? null); setShowGfExplanation(true); }}
              onRisks={(row) => setRiskStrategy(row)}
              renderDetail={renderDetail}
              onAllWays={() => setShowAllSeStrategies(true)}
              onChanges={() => { setGfAsking(true); scrollAppToTop(); }}
              onBuildYourOwn={(row) => { if (row) seedManualFrom(row); else openGfBlank(); setStrategyMode('manual'); scrollAppToTop(); }}
              onAdvice={() => { const row = gfStrategies.find((x) => x.id === chosenIdNow); if (row) void generateGfSoa(row); }}
              onFamilyGuide={() => openGuide('guide')}
              onIncomeOnly={setIncomeOnlyUnderstood ?? undefined}
            />
          )}
        </div>
      )}

      {step === 3 && strategyMode === 'manual' && (
        <div className="mt-8">
          <GfBuildPlan
            factFind={factFind}
            setFactFind={setFactFind}
            horizonYears={Number(localLegacyYears) || 10}
            startedFrom={manualStartedFrom}
            customising={!!manualCustomisedFrom}
            onClear={openGfBlank}
            startOptions={[
              ...gfGoalLeaders.slice(0, 3).map((l) => ({ id: String(l.id), label: gfPlanWords(l, factFind).title })),
              { id: 'blank', label: 'A blank plan' },
            ]}
            onStartFrom={(id) => {
              if (id === 'blank') startBlankManualPlan();
              else { const row = gfStrategies.find((x) => String(x.id) === id); if (row) seedManualFrom(row); }
            }}
            form={manualForm}
            onForm={(f) => {
              manualStash.current[manualForm] = { amountPaid: num(gfInputs.amountPaid), constructionCost: num(gfInputs.constructionCost) };
              const back = manualStash.current[f];
              setManualForm(f);
              setGfInputs((prev) => ({
                ...prev,
                justMovingIn: f === 'nothing',
                transferHome: f === 'home',
                // Cash comes back as it was, or starts at the limit; cash on top of a build or the home starts at nothing.
                amountPaid: f === 'nothing' ? 0 : back ? back.amountPaid : f === 'cash' ? Math.floor(Number(reasonableValueToday) || 0) : 0,
                constructionCost: f === 'build' ? (back ? back.constructionCost : (gfGiveOf(factFind) === 'build' ? num(goals.gfKnownAmount) : 0)) : 0,
                fundFrom: f === 'home' || f === 'nothing' ? 'savings' : prev.fundFrom,
                funding: {},
              }));
              noteManualChange(f === 'cash' ? 'Cash for the right' : f === 'build' ? 'A build paid for' : f === 'home' ? 'The home signed over' : 'Moving in, paying nothing');
            }}
            cash={num(gfInputs.amountPaid)}
            onCash={(n) => { setField('amountPaid', n); noteManualChange(`Cash → ${formatCurrency(n)}`); }}
            build={num(gfInputs.constructionCost)}
            onBuild={(n) => { setField('constructionCost', n); noteManualChange(`The build → ${formatCurrency(n)}`); }}
            reasonableValue={Number(reasonableValueToday) || reasonableValue}
            entryContribution={entryContribution}
            interestValue={interestValue}
            deprivation={deprivation}
            homeUse={homeUse}
            onHomeUse={(u) => {
              setGfInputs((prev) => ({ ...prev, fundFrom: u === 'sold' ? 'sell_home' : 'savings', keptHomeUse: u === 'rented' ? 'rented' : 'vacant', funding: {}, deployment: u === 'sold' ? prev.deployment : {} }));
              noteManualChange(u === 'sold' ? 'The home is sold' : u === 'rented' ? 'The home is kept and rented out' : 'The home is kept, empty');
            }}
            rent={{ weekly: num(gfInputs.keptHomeRent), costs: num(gfInputs.keptHomeExpenses), vacancy: gfInputs.keptHomeVacancy }}
            onRent={(patch) => {
              setGfInputs((prev) => ({ ...prev, ...(patch.weekly != null ? { keptHomeRent: patch.weekly } : {}), ...(patch.costs != null ? { keptHomeExpenses: patch.costs } : {}), ...(patch.vacancy != null ? { keptHomeVacancy: patch.vacancy } : {}) }));
              noteManualChange('The rent, its costs and the weeks empty');
            }}
            saleResidual={saleResidual}
            deployment={deployment}
            onDeployment={(d) => { setField('deployment', d); noteManualChange('Where the money from the sale goes'); }}
            cashFromSavings={cashFromSavings}
            fundingSuggested={fundingSuggested}
            reserve={manualReserve}
            funding={funding}
            onFunding={(m) => { setField('funding', m); noteManualChange('Where the money comes from'); }}
            cgtOverrides={fundingCgtOverrides}
            onCgtOverrides={(o) => setField('fundingCgtOverrides', o)}
            fundingShortfall={panelModel.fundingShortfall}
            working={gfCalcWorking}
            effect={{
              cashflow: manualImpactReady && projections.length ? num(projections[0]?.netCashflow ?? projections[0]?.cashFlow) : null,
              baseCashflow: todayFinancials ? num(stayPutProjections[0]?.netCashflow ?? todayFinancials.cashFlow) : null,
              pension: manualImpactReady && after?.socialSecurity != null ? num(after.socialSecurity) : null,
              basePension: todayFinancials ? num(todayFinancials.socialSecurity) : null,
              legacy: manualImpactReady && projections.length ? num(legacyEstimate) : null,
              baseLegacy: manualBaselineLegacy,
            }}
            lastChange={manualLastChange}
            onExplain={() => { setExplainSeId(null); setShowGfExplanation(true); }}
            onRisks={() => setRiskStrategy({ ...(manualPlanView || { id: manualPlanId, name: 'Your granny flat arrangement' }), __move: { propertyTransferred, entryContribution } } as unknown as GfStrategyView)}
            onChoose={chooseManualPlan}
            chosen={manualChosen}
            chooseHeld={manualIncomeOnlyHeld || !manualPlanView}
            beforeChoose={manualIncomeOnly && setIncomeOnlyUnderstood ? <IncomeOnlyBeforeChoose factFind={factFind} amount={Number(deployment?.annuity) || 0} onChange={setIncomeOnlyUnderstood} action="saving" /> : undefined}
            familyPanel={manualGuaranteed ? (
              <AnnuityFamilyPanel
                factFind={factFind}
                personName={manualGuaranteed.personName}
                amount={manualGuaranteed.amount}
                kind={manualGuaranteed.kind}
                incomeAYear={manualGuaranteed.incomeAYear}
                rows={panelModel.projections as never}
                horizonYears={Number(localLegacyYears) || 10}
                testId="gf-manual-family-panel"
              />
            ) : undefined}
            onBackToPlans={() => { setStrategyMode('optimised'); scrollAppToTop(); }}
            onYearByYear={() => setSeDeepDive({ projections: panelModel.projections, name: manualPlanView ? gfPlanWords(manualPlanView, factFind).title : 'Your granny flat arrangement' })}
            comparison={(
              <div className="mt-3 border-t border-slate-200 pt-2 text-[13px] text-slate-700" data-testid="gf-build-compare">
                {!manualComparison ? (
                  <button type="button" onClick={runManualComparison} className="font-semibold text-brand-navy underline decoration-slate-300 underline-offset-4">Compare with the plan that leaves the most</button>
                ) : manualComparison.loading ? (
                  <span className="text-slate-500">Testing the ways to set it up…</span>
                ) : manualComparison.best ? (
                  <span>The tested plan that leaves the most in {voice.possessive} estate, <strong>{gfPlanWords(manualComparison.best, factFind).title.replace(/^./, (c) => c.toLowerCase())}</strong>, leaves {formatCurrency(Math.round(num(manualComparison.best.snapshot?.legacyValue)))} after about {Number(localLegacyYears) || 10} years{manualImpactReady && projections.length ? `, against ${formatCurrency(Math.round(num(legacyEstimate)))} for this plan` : ''}. That doesn&apos;t make it right for {voice.them} — it&apos;s a comparison, not a switch.</span>
                ) : (
                  <span className="text-slate-500">No other way of setting it up could be tested.</span>
                )}
              </div>
            )}
            debtCard={payableDebtsOf(factFind as never).length > 0 ? (
              <DebtPayoffStrategy
                variant="section"
                factFind={factFind as never}
                debtPayments={debtPayments}
                setDebtPayments={(v) => { setDebtPayments(v); noteManualChange('Debts cleared'); }}
                debtFunding={debtFunding}
                setDebtFunding={setDebtFunding}
                repaymentBasis={debtRepaymentBasis}
                setRepaymentBasis={setDebtRepaymentBasis}
                competingUseNote="The money comes from the same assets that can pay for the right, so what is spent here reduces what is left to pay or build with."
              />
            ) : undefined}
          />
          {manualChosen && manualPlanView && (
            <div className="mt-5 flex flex-wrap items-center gap-4 rounded-2xl border border-slate-200 border-l-4 border-l-emerald-500 bg-white px-5 py-4 shadow-card" data-testid="gf-manual-chosen">
              <p className="min-w-0 flex-1 text-[14px] text-slate-700"><strong className="text-slate-900">This plan is saved with {voice.possessive} file as the chosen plan.</strong> The advice document is written from it.</p>
              <button type="button" onClick={() => { setGfView('hub'); scrollAppToTop(); }} className="text-[14px] font-semibold text-brand-navy underline decoration-slate-300 underline-offset-4">Go to the granny flat plan</button>
              <button type="button" onClick={generateManualSoa} className="rounded-lg bg-blue-600 px-4 py-2.5 text-[14px] font-semibold text-white hover:bg-blue-700" data-testid="gf-manual-advice">Get the advice document →</button>
            </div>
          )}
        </div>
      )}

      {/* EVERY WAY TESTED, WORDED FOR THE READER: each row says the plan as its card does, against not entering. */}
      {showAllSeStrategies && gfSuggestions && (() => {
        const stay = gfStayPut;
        const pick = (s: GfStrategyView) => allSortBy === 'cashflow' ? num(s.snapshot?.netCashflow) : allSortBy === 'pension' ? num(s.snapshot?.pension) : num(s.snapshot?.legacyValue);
        const sorted = gfStrategies.filter((s) => !isGfBaseline(s)).sort((a, b) => pick(b) - pick(a));
        const signedCell = (d: number | null) => d == null || Math.round(d) === 0 ? null : <span className={`block text-[12px] ${d < 0 ? 'text-rose-700' : 'text-emerald-700'}`}>{d < 0 ? '−' : '+'}{formatCurrency(Math.abs(Math.round(d)))}</span>;
        return (
          <DialogShell titleId="gf-all-ways-title" title={`Every way we tested, ${sorted.length}`} eyebrow={`Against staying put, over about ${localLegacyYears} years`} onClose={() => setShowAllSeStrategies(false)} size="xl" testId="gf-all-ways-dialog" flush>
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-5 py-3">
              <p className="text-[14px] text-slate-700">Every way of setting up the arrangement we worked through for {voice.them}. The estate figure is what is left in {voice.possessive} estate; what is given to {host} is beside it, not lost.</p>
              <label className="flex items-center gap-2 text-[13px] text-slate-600">Order by
                <select value={allSortBy} onChange={(e) => setAllSortBy(e.target.value as 'legacy' | 'cashflow' | 'pension')} className="rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-[13px]">
                  <option value="legacy">In the estate</option>
                  <option value="cashflow">Left over each year</option>
                  <option value="pension">Age Pension</option>
                </select>
              </label>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-[14px]">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-[12px] font-semibold text-slate-500">
                    <th className="px-5 py-3">The plan</th>
                    <th className="px-5 py-3 text-right">In the estate</th>
                    <th className="px-5 py-3 text-right">Given to {host}</th>
                    <th className="px-5 py-3 text-right">Age Pension, a year</th>
                    <th className="px-5 py-3 text-right">Left over, first year</th>
                    <th className="px-5 py-3"></th>
                  </tr>
                </thead>
                <tbody className="tabular-nums">
                  {stay && (
                    <tr className="border-b border-slate-100 bg-slate-50">
                      <td className="px-5 py-3"><p className="font-semibold text-slate-900">Staying put — not entering the arrangement</p><p className="mt-0.5 text-[12.5px] text-slate-500">What every plan is measured against.</p></td>
                      <td className="px-5 py-3 text-right">{formatCurrency(stay.snapshot?.legacyValue)}</td>
                      <td className="px-5 py-3 text-right">—</td>
                      <td className="px-5 py-3 text-right">{formatCurrency(stay.snapshot?.pension)}</td>
                      <td className="px-5 py-3 text-right">{num(stay.snapshot?.netCashflow) < 0 ? `short ${formatCurrency(-num(stay.snapshot?.netCashflow))}` : formatCurrency(stay.snapshot?.netCashflow)}</td>
                      <td className="px-5 py-3"></td>
                    </tr>
                  )}
                  {sorted.map((s) => {
                    const isSel = s.id === chosenIdNow;
                    const w = gfPlanWords(s, factFind);
                    const cashNow = num(s.snapshot?.netCashflow);
                    const gi = gfGuaranteedIncomeOf(s, factFind);
                    return (
                      <tr key={s.id || s.name} className={`border-b border-slate-100 ${isSel ? 'bg-blue-50/60' : ''}`}>
                        <td className="px-5 py-3">
                          <p className="font-semibold text-slate-900">{w.title}</p>
                          <p className="mt-0.5 max-w-xl text-[12.5px] text-slate-500">{w.sub}</p>
                          {gi?.incomeOnly && setIncomeOnlyUnderstood && !isSel && <div className="mt-2 max-w-xl"><IncomeOnlyBeforeChoose factFind={factFind} amount={gi.amount} onChange={setIncomeOnlyUnderstood} /></div>}
                        </td>
                        <td className="px-5 py-3 text-right font-semibold">{formatCurrency(s.snapshot?.legacyValue)}{signedCell(stay ? num(s.snapshot?.legacyValue) - num(stay.snapshot?.legacyValue) : null)}</td>
                        <td className="px-5 py-3 text-right">{formatCurrency(gfGivenOf(gfMoveOf(s, factFind)))}</td>
                        <td className="px-5 py-3 text-right">{formatCurrency(s.snapshot?.pension)}{signedCell(stay ? num(s.snapshot?.pension) - num(stay.snapshot?.pension) : null)}</td>
                        <td className={`px-5 py-3 text-right ${cashNow < 0 ? 'text-rose-700' : ''}`}>{cashNow < 0 ? `short ${formatCurrency(-cashNow)}` : formatCurrency(cashNow)}</td>
                        <td className="whitespace-nowrap px-5 py-3 text-right">
                          <div className="inline-flex gap-2">
                            <Button variant={isSel ? 'secondary' : 'brand'} size="sm" disabled={!isSel && engineIncomeOnlyHeld(s)} onClick={() => { selectEngineStrategy(s); setShowAllSeStrategies(false); }}>{isSel ? 'Chosen' : 'Choose this plan'}</Button>
                            <Button variant="secondary" size="sm" onClick={() => { setSeDeepDive({ projections: s.projections || [], name: w.title, id: s.id }); setShowAllSeStrategies(false); }}>Year by year</Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </DialogShell>
        );
      })()}

      {/*
        * "EXPLAIN THIS" AS AGED CARE TELLS IT (owner, 22 Oct 2026: "all modules use consistent UI"). The plan's words as
        * the title, the numbered cause→effect lines (gfExplainSteps), "Put together" carrying where the money comes from,
        * then "=" the estate against staying put — the explained plan's OWN staying-put run (gfStayPut for a tested plan,
        * not entering as built by hand for the hand-built one), so the "=" line and the plans screen agree. Then the
        * pieces of the plan, the family panel and the debts. One path for a tested plan and the hand-built one.
        */}
      {showGfExplanation && (() => {
        const plan = explainStrategy ?? manualPlanView;
        const recapModel = explainView ?? panelModel;
        const basePlan = explainStrategy ? gfStayPut : manualStayPutView;
        const horizon = Number(localLegacyYears) || 10;
        const story = plan ? gfExplainSteps(plan, basePlan, factFind, horizon) : null;
        const g = gfGuaranteedIncomeOf(plan, factFind);
        const compare = explainStrategy && g ? gfComparePlanFor(explainStrategy, gfStrategies) : null;
        const rows = ((plan?.projections) || []) as ProjectionYear[];
        const moneyStory = summariseFunding(rows as never).hasDeficit
          ? buildMoneyStory({ rows: rows as never, factFind, runway: planRunwayCopy(rows as never, rows.length), deposit: { paid: 0, refundTo: '' }, horizonYears: horizon, enteringName: factFind?.client?.firstName || 'The household', spentOn: 'the granny flat' })
          : null;
        return (
          <PlanExplanationModal
            eyebrow="Explain this"
            title={plan ? gfPlanWords(plan, factFind).title : 'Your granny flat arrangement'}
            intro={`Each line says what ${voice.they} do, and what that does to the numbers. "Staying put" means not entering the arrangement and changing nothing else.`}
            onClose={() => { setShowGfExplanation(false); setExplainSeId(null); }}
            footer={(
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  setShowGfExplanation(false);
                  if (explainStrategy) setSeDeepDive({ projections: explainStrategy.projections || [], name: gfPlanWords(explainStrategy, factFind).title, id: explainStrategy.id });
                  else setSeDeepDive({ projections: panelModel.projections, name: 'Your granny flat arrangement' });
                  setExplainSeId(null);
                }}
              >
                See the year-by-year figures
              </Button>
            )}
          >
            {story && plan && (
              <CauseEffectLedger
                testId="gf-explain-steps"
                // What each piece IS, above what it does (owner, 24 Oct 2026).
                leverKinds={(() => { const row = explainStrategy ?? manualPlanView; const mv = row ? gfMoveOf(row, factFind) : null; return gfRiskKinds(explainStrategy ? String(explainStrategy.id || '') : manualPlanId, { construction: mv?.construction, amountPaid: mv?.amountPaid, home: mv?.home }); })()}
                lines={[
                  ...story.steps.map((t, i) => { const sc = story.stepChanges[i]; return sc ? <>{sc.before}<ChangeOrText change={sc.change} text={null} /></> : t; }),
                  putTogetherLine({
                    cashflow: story.cashflow,
                    baseCashflow: story.baseCashflow,
                    they: voice.they,
                    baseWord: 'staying put',
                    // Where a short year is drawn from, account by account, inside "Put together" (owner, 21–22 Oct 2026).
                    moneyStory: moneyStory ? <span data-testid="gf-money-story-section"><MoneyStoryBlock story={moneyStory} testId="gf-money-story" /></span> : null,
                  }),
                ]}
                estate={story.estate}
                baseEstate={story.baseEstate}
                horizonYears={horizon}
                baseWord="staying put"
              >
                {story.givenNote && <p className="mt-1 text-[14px] leading-relaxed text-slate-600" data-testid="gf-explain-given-note">{story.givenNote} It is with {gfHostPhrase(factFind)} — not lost, but no longer in {voice.their} estate.</p>}
                {/* The pieces of the plan, one line each with "what's this?" — no "Your plan summary" heading here. */}
                <GrannyFlatPlanRecap model={recapModel} firstName={factFind?.client?.firstName} baselineLegacy={story.baseEstate} legacyYears={localLegacyYears} factFind={factFind} piecesOnly />
                {g && (
                  <div className="mt-5" data-testid="gf-explain-family-section">
                    <DialogSection>If {g.personName} passes away</DialogSection>
                    <div className="mt-2">
                      <AnnuityFamilyPanel
                        factFind={factFind}
                        personName={g.personName}
                        amount={g.amount}
                        kind={g.kind}
                        incomeAYear={g.incomeAYear}
                        rows={(plan.projections || []) as never}
                        compareRows={(compare?.projections || null) as never}
                        compareNote={compare ? GF_COMPARE_NOTE : null}
                        horizonYears={horizon}
                        testId="gf-explain-family-panel"
                      />
                    </div>
                  </div>
                )}
                {(() => {
                  // The debts this plan keeps, and what the same plan with them paid off did ("<id>-clear-debt").
                  const isManual = !explainStrategy;
                  const fig = (p: { snapshot?: Record<string, unknown> | null } | null | undefined) => ({ estate: Number(p?.snapshot?.legacyValue) || 0, pension: Number(p?.snapshot?.pension) || 0, cashflow: Number(p?.snapshot?.netCashflow) || 0 });
                  const twin = isManual ? null : (gfStrategies || []).find((s) => s.id === `${explainStrategy!.id}-clear-debt`) ?? null;
                  const cleared = isManual
                    ? Object.values((debtPayoffPlan?.payments || {}) as Record<string, unknown>).reduce<number>((t, x) => t + (Number(x) || 0), 0)
                    : /-clear-debt$/.test(String(explainStrategy!.id)) ? clearableDebtTotal(factFind) : 0;
                  return <DebtsInPlanNote factFind={factFind} clearedAmount={cleared} plan={fig(plan as never)} twin={twin ? fig(twin as never) : null} years={horizon} context={isManual ? 'manual' : 'tested'} />;
                })()}
                {/* The home kept and let: the six-year capital gains tax rule, whatever the years planned (22 Oct 2026). */}
                {plan && gfMoveOf(plan, factFind).home === 'rented' && <RentedHomeCgtNote horizonYears={Number(localLegacyYears) || 10} they={voice.they} />}
                {/* Money the plan invests: the mix, and how it is usually held (23 Oct 2026). */}
                {plan && plansInvest(gfPlanWords(plan, factFind).sub) && <HowItIsInvestedNote factFind={factFind} />}
              </CauseEffectLedger>
            )}
          </PlanExplanationModal>
        );
      })()}

      {riskStrategy && (() => {
        const m = gfMoveOf(riskStrategy, factFind);
        const kinds = (riskStrategy as { __kinds?: ReturnType<typeof riskKindsFromCandidateId> }).__kinds ?? gfRiskKinds(String(riskStrategy.id || ''), { construction: m.construction, amountPaid: m.amountPaid, home: m.home });
        const move = (riskStrategy as { __move?: { propertyTransferred: number; entryContribution: number } }).__move ?? { propertyTransferred: m.propertyTransferred, entryContribution: m.entryContribution };
        return (
          <StrategyRisksPanel
            voice={voice}
            strategyName={riskStrategy.inputs ? gfPlanWords(riskStrategy, factFind).title : (riskStrategy.name || '')}
            onClose={() => setRiskStrategy(null)}
            risks={risksFor(kinds, move, gfAnnuityHorizonWarningOf(riskStrategy, factFind), Number((riskStrategy.inputs?.deployment as { ipStampDuty?: number } | undefined)?.ipStampDuty) || null)}
          />
        );
      })()}

      {showStayPutProjections && (
        <ProjectionsModal
          onClose={() => setShowStayPutProjections(false)}
          title="Staying put — not entering the arrangement"
          comparedOverYears={Number(localLegacyYears) || undefined}
          projections={stayPutProjections as never}
          factFind={factFind as never}
          initialFinancials={(todayFinancials ?? before) as never}
          costLabel={null}
        />
      )}

      {seDeepDive && (
        <ProjectionsModal
          onClose={() => setSeDeepDive(null)}
          title={seDeepDive.name}
          comparedOverYears={Number(localLegacyYears) || undefined}
          projections={(modalPeriodMode === 'calendar_year' && calendarYearProjections ? calendarYearProjections : seDeepDive.projections) as never}
          {...(deepDiveCandidate ? { onPeriodModeChange: setModalPeriodMode, isReloading: modalPeriodMode === 'calendar_year' && !calendarYearProjections } : {})}
          factFind={factFind as never}
          initialFinancials={(todayFinancials ?? before) as never}
          costLabel={null}
        />
      )}
    </div>
  );
};

interface GrannyFlatPageProps {
  setActivePage: (page: string) => void;
  factFind: DraftFactFind;
  /** App-owned saved strategies: the chosen granny flat plan is read from here. */
  savedStrategies?: ReadonlyArray<Record<string, unknown>>;
  /** App-owned saved strategies — choosing a plan saves here. */
  setSavedStrategies?: React.Dispatch<React.SetStateAction<Array<Record<string, unknown>>>>;
  /** Persists answers to the client record (App owns the fact-find state). */
  setFactFind?: React.Dispatch<React.SetStateAction<DraftFactFind>>;
  /**
   * Called with each generated Statement of Advice (the strategy plus its document snapshot) so it
   * lands on the server's advice register like aged care's (owner, 2 Oct 2026). App.tsx decides where.
   */
  onSoaGenerated?: (document: Record<string, unknown>) => void;
}

const GrannyFlatPage = ({ setActivePage, factFind, savedStrategies, setSavedStrategies, setFactFind, onSoaGenerated }: GrannyFlatPageProps) => {
  /*
   * WHERE THE PAGE OPENS (review, 18 Oct 2026). Home's "Open the guide" and the action plan's "Solicitor
   * brief" / "Family discussion guide" hand a section over in sessionStorage (openGrannyFlatSection); read
   * once, on mount, then cleared. Otherwise the plan page, once a plan is chosen, else step 1.
   */
  const [opening, setOpening] = useState(() => takeGrannyFlatOpenSection());
  /** Bumped to start the module over: remounts the analyser, so nothing half-entered survives. */
  const [startKey, setStartKey] = useState(0);
  useEffect(() => { scrollAppToTop(); }, []);
  /*
   * CHOOSING "Granny flat" IN THE MENU FROM INSIDE THE MODULE RETURNS TO THE START (review, 18 Oct 2026): the
   * layout fires PAGE_RESELECTED_EVENT (see gfPresentation.ts). A section handed over at the same moment wins.
   */
  useEffect(() => {
    const onReselect = (e: Event) => {
      const page = (e as CustomEvent<{ page?: string }>).detail?.page;
      if (page && page !== 'GrannyFlatPage') return;
      setOpening(takeGrannyFlatOpenSection());
      setStartKey((k) => k + 1);
      scrollAppToTop();
    };
    window.addEventListener(PAGE_RESELECTED_EVENT, onReselect);
    return () => window.removeEventListener(PAGE_RESELECTED_EVENT, onReselect);
  }, []);
  return (
    <GrannyFlatAnalyser
      key={`analyser-${startKey}`}
      setActivePage={setActivePage}
      factFind={factFind}
      savedStrategies={savedStrategies}
      setSavedStrategies={setSavedStrategies}
      setFactFind={setFactFind}
      onSoaGenerated={onSoaGenerated}
      opening={opening}
    />
  );
};

export default GrannyFlatPage;
